import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  cancelarCotacao,
  encerrarRespostas,
  enviarCotacao,
  enviarEmailAutorizacaoFaturamento,
  enviarEmailEspelhoNf,
  enviarEmailResumoProposta,
  fecharCesta,
  fetchComparacao,
  reabrirCotacao,
  recotarItens,
  fetchCotacao,
  fetchEmailsFornecedor,
  fetchPedidos,
  gerarPedidos,
  obterLinkWhatsapp,
  obterStatusOctadesk,
  enviarWhatsappConvite,
  reenviarConvite,
  reenviarPedido,
  rotacionarConvite,
  salvarDecisoes,
} from '../../../api/portalCotacoes/portalCotacoesApi';
import {
  Comparacao,
  CotacaoDetalhe,
  DecisaoPayload,
  EmailFornecedorEnviado,
  EnvioDeEmail,
  FinalidadeEmailFornecedor,
  ItemComparado,
  PedidoGerado,
  PropostaComparada,
  ResultadoDisparo,
  StatusOctadesk,
} from '../../../interfaces/PortalCotacoes';
import { notify } from '../../../shared/feedback/notify';
import { obterPortalSocket } from '../../../api/portalCotacoes/socket';
import { CotacaoBadge, ConviteBadge, PrazoVencidoBadge, prazoVencido } from '../components/PortalBadges';
import ConfirmacaoModal from '../components/ConfirmacaoModal';
import GerarPedidosModal from '../components/GerarPedidosModal';
import ResultadoDisparoModal, { LinhaDisparo } from '../components/ResultadoDisparoModal';
import PropostaRecebidaModal from '../components/PropostaRecebidaModal';
import Referencias from '../components/Referencias';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import EnvioPorCanal from '../components/EnvioPorCanal';
import ConviteAcoesModal from '../components/ConviteAcoesModal';
import AnexosSection from '../components/AnexosSection';
import FotoVeiculoSection, { EstadoDaFotoDoVeiculo } from '../components/FotoVeiculoSection';
import FotosFornecedorSection from '../components/FotosFornecedorSection';
import ConvidarFornecedorModal from '../components/ConvidarFornecedorModal';
import ItensSemOfertaFaixa from '../components/ItensSemOfertaFaixa';
import HistoricoCorrecoesModal from '../components/HistoricoCorrecoesModal';
import { useAlcanca } from '../../../hooks/usePapeis';
import { useArrastarParaRolar } from '../../../hooks/useArrastarParaRolar';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import '../../../components/como-funciona/help-circle-btn.css';
// A tela monta o esqueleto com as classes compartilhadas (recepcao-conte-do,
// recepcao-header-row, recepcao-title), entao importa a folha que as define em vez
// de contar com outra tela te-la carregado antes (BUG-V74/at-67).
import '../../recepcao/pages/Recepcao.css';
import './PortalCotacoes.css';
import './PortalCotacaoDetalhe.css';
// Casco do modal de justificativa: o mesmo dos modais de fornecedor e de proposta (por import).
import '../components/FornecedorDetalhesModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema
import '../../../shared/feedback/aviso-de-tela.css';

const helpPainel: ComoFuncionaSecao[] = [
  {
    titulo: 'Convites e links',
    conteudo: (
      <div>
        <p>
          Cada fornecedor tem um <strong>link exclusivo</strong>, guardado de forma cifrada: você pode consultá-lo
          quantas vezes quiser sem invalidá-lo. Só <strong>uma</strong> ação troca o endereço —{' '}
          <strong>Gerar novo link</strong>, que é para quando o link precisa deixar de valer (foi para o contato
          errado, por exemplo).
        </p>
        <p>
          As ações de cada fornecedor ficam no botão <strong>Ações</strong>, na última coluna da tabela. Ao abrir,
          você já vê o link que está valendo (com botão de copiar) e o desfecho do envio. O convite sai{' '}
          <strong>só pelo WhatsApp</strong> (decisão do teste com Compras, 29/10/2026), e o link{' '}
          <strong>não pede senha</strong>: quem abre o link vê a cotação, por isso ele não deve ser repassado.
        </p>
        <p>
          O <strong>WhatsApp tem sempre uma única ação</strong>, e ela muda sozinha conforme a integração de
          mensagens estiver ativa ou não. Com a integração <strong>ativa</strong>, o botão é{' '}
          <strong>Enviar WhatsApp</strong>: o sistema envia agora, pelo número oficial da empresa, mantendo o link.
          Com ela <strong>desligada</strong>, o botão é <strong>WhatsApp</strong>: abre a conversa com a mensagem
          pronta, e o envio é feito por você, do seu próprio aparelho — esse caminho gera um link novo. Nunca
          aparecem os dois ao mesmo tempo.
        </p>
        <p>
          A coluna <strong>Envio</strong> diz o que aconteceu com cada canal: <strong>enviado</strong> (saiu),{' '}
          <strong>falhou</strong> (tentou e não conseguiu — passe o mouse para ver o motivo) e{' '}
          <strong>a enviar</strong> (o link ficou pronto e espera você despachar pelo WhatsApp). Repare que ela é
          diferente da coluna <strong>Status</strong>: aquela conta em que ponto o convite está, e passa a
          "Enviado" assim que a cotação é enviada, mesmo que um canal tenha falhado.
        </p>
        <p>
          Convites criados antes de 12/08/2026 não têm o link recuperável: o que o fornecedor recebeu continua
          funcionando, mas o modal de ações não consegue exibi-lo — nesse caso a saída é gerar um novo.
        </p>
        <p>
          Depois de cada ação, a tela mostra <strong>o que saiu, para qual contato e o que falhou</strong> — um
          e-mail que não pôde ser enviado aparece com o motivo, e o link fica visível para entrega manual.
        </p>
      </div>
    ),
  },
  {
    titulo: 'Ver a proposta de quem respondeu',
    conteudo: (
      <p>
        Quando um fornecedor responde, o <strong>nome dele vira um link</strong> na tabela — o clique abre a
        proposta como ele enviou: preço e prazo por peça, marca, condição de pagamento, o frete e o total dos
        itens cotados. A comparação lado a lado entre fornecedores continua sendo a aba{' '}
        <strong>Comparação</strong>, liberada ao encerrar as respostas. Quando o fornecedor corrigiu a proposta,
        o texto <strong>correção N: ver o que mudou</strong> abre o que ele alterou em cada envio, com o valor de
        antes e o de depois.
      </p>
    ),
  },
  {
    titulo: 'Convidar fornecedor depois do envio',
    conteudo: (
      <p>
        Enquanto a cotação recebe respostas, <strong>Convidar fornecedor</strong> inclui mais um fornecedor sem
        refazer a cotação. O convite sai na hora, pelo WhatsApp, com o mesmo prazo. Fornecedor sem WhatsApp
        cadastrado não pode ser escolhido. Pelo botão de <strong>Itens</strong> o convite pode levar só parte das
        peças: o fornecedor vê e cota só as que receber. Quem já está na cotação não recebe peças a mais; chame
        outro fornecedor.
      </p>
    ),
  },
  {
    titulo: 'Itens sem oferta',
    conteudo: (
      <p>
        Quando uma peça ainda não tem oferta de ninguém, a faixa no topo da cotação diz qual é e se é porque
        ninguém respondeu ainda ou porque quem respondeu não tinha a peça. Dali você convida outro fornecedor só
        para essas peças, sem fechar a cotação. Ao clicar em <strong>Encerrar respostas</strong>, a confirmação
        lembra das peças sem oferta e oferece convidar antes; encerrar assim mesmo continua possível. Na
        comparação, <strong>Não recebeu</strong> é o fornecedor a quem a peça não foi enviada, diferente de{' '}
        <strong>Não cotou</strong> (recebeu e não ofertou).
      </p>
    ),
  },
  {
    titulo: 'Reabrir para respostas',
    conteudo: (
      <p>
        Em negociação, <strong>Reabrir para respostas</strong> volta a aceitar propostas até a nova data: os
        fornecedores podem responder e corrigir, e você pode convidar outro. As escolhas já feitas na comparação
        ficam como estão.
      </p>
    ),
  },
  {
    titulo: 'Fotos que o fornecedor vê',
    conteudo: (
      <p>
        A <strong>foto do veículo</strong> vai na prévia do link e no topo da tela do fornecedor; dá para escolher
        do checklist ou enviar do computador. Em <strong>Fotos para o fornecedor</strong> entram até 10 fotos
        (dano, veículo em oficina externa), que ele também vê — ligadas a uma peça, aparecem naquela peça. Já{' '}
        <strong>Imagens (uso interno)</strong> são só da equipe e o fornecedor nunca as vê.
      </p>
    ),
  },
  {
    titulo: 'Chassi, local de entrega e Atualizar',
    conteudo: (
      <p>
        O chassi, o local de entrega e o <strong>Motivo da Requisição</strong> (o texto que Compras lê no
        Nasajon) vêm da requisição quando a cotação é criada; "Não informado" quer dizer que a requisição não
        os trazia. <strong>Atualizar</strong> recarrega a cotação na hora, e o horário ao
        lado diz quando foi a última carga.
      </p>
    ),
  },
  {
    titulo: 'Encerrar respostas e iniciar negociação',
    conteudo: (
      <p>
        Ao encerrar, os links <strong>param de aceitar propostas</strong> e a comparação é liberada. O fornecedor
        que abrir o link depois disso vê que a Atual Clube já encerrou o recebimento. O prazo é uma data e vale
        até o fim daquele dia; passado o prazo, as respostas continuam chegando até você encerrar.
      </p>
    ),
  },
  {
    titulo: 'A tela se atualiza sozinha',
    conteudo: (
      <p>
        Com esta tela aberta, o movimento dos fornecedores aparece <strong>sem recarregar</strong>: quando alguém
        abre o link, visualiza a cotação, envia ou corrige a proposta, a situação e a contagem de respostas mudam
        na hora. O mesmo vale para o <strong>encerramento automático</strong> quando o prazo termina. Se a conexão
        cair, a tela deixa de acompanhar sozinha — nesse caso, recarregar a página mostra a situação atual.
      </p>
    ),
  },
];

const helpComparacao: ComoFuncionaSecao[] = [
  {
    titulo: 'O score é apoio, não decisão',
    conteudo: (
      <p>
        O score combina <strong>preço e prazo</strong> conforme o preset da cotação e serve para ordenar a leitura.
        Quem decide é você, item a item — inclusive contra o score.
      </p>
    ),
  },
  {
    titulo: 'A regra do menor preço',
    conteudo: (
      <p>
        Toda escolha que <strong>não seja o menor preço</strong> exige justificativa e passa pela aprovação do
        gestor de compras — <strong>mesmo quando o score aponta para ela</strong>. Quando a escolha é a do
        preset, a justificativa padrão dele já vale: ao salvar o rascunho, a tela mostra o texto de cada item e
        pede confirmação. Para trocar o texto, clique na proposta escolhida. Escolha fora do preset exige que você escreva o motivo.
      </p>
    ),
  },
  {
    titulo: 'Marcadores e ausências',
    conteudo: (
      <p>
        <strong>[P]</strong> marca o menor preço do item e <strong>[S]</strong> o melhor score.{' '}
        <strong>Não cotou</strong> significa que o fornecedor respondeu mas não tem aquela peça;{' '}
        <strong>Sem resposta</strong> é quem não respondeu nada. Quando só um fornecedor cota o item, aparece{' '}
        <strong>proposta única</strong> em vez de score — score de candidato sozinho não informa nada.
      </p>
    ),
  },
  {
    titulo: 'O estado de cada item junto ao gestor',
    conteudo: (
      <p>
        Depois que a cesta é enviada ao gestor, cada item carrega uma etiqueta com o estado dele:{' '}
        <strong>Aguardando aprovação</strong> (escolha acima do menor preço, na fila do gestor),{' '}
        <strong>Aprovado</strong>, <strong>Reprovado</strong> (com o comentário do gestor logo abaixo) ou{' '}
        <strong>Fechado sem divergência</strong> (menor preço ou sem compra — nunca precisou do gestor). A
        reprovação reabre <strong>somente o item reprovado</strong>: ele é o único que volta a aceitar escolha,
        e os demais ficam travados — o que já foi aprovado não se desfaz e não volta a ser cotado. Ao fechar a
        cesta de novo, se a nova escolha ainda não for o menor preço, o item entra{' '}
        <strong>numa nova aprovação</strong> do gestor.
      </p>
    ),
  },
  {
    titulo: 'Imagens de uso interno',
    conteudo: (
      <div>
        <p>
          Você pode anexar fotos da peça ou referências à cotação, e vincular cada imagem a uma peça
          específica. <strong>O fornecedor não vê</strong> — nada vai para o link dele; as imagens ficam num
          armazenamento privado e só quem tem acesso a esta tela as abre.
        </p>
        <p>
          Fotos de iPhone (HEIC) são convertidas sozinhas. Imagens acima de 10 MB não são aceitas, e a tela diz
          quais ficaram de fora em vez de falhar em silêncio.
        </p>
        <p>
          <strong>Remover</strong> tira a imagem daqui, mas o registro de que ela existiu — e de quem a removeu
          — fica na Auditoria. Se a seção aparecer desabilitada, o armazenamento não está configurado neste
          ambiente: acione a TI.
        </p>
      </div>
    ),
  },
];

/**
 * `?` localizado da legenda (13/08/2026 — pedido do Victor): como o score é calculado.
 *
 * Fica separado do `helpComparacao` (o drawer da aba inteira) porque responde uma pergunta
 * específica — "de onde saiu esse 0,918?" —, e quem a faz está olhando a célula, não
 * procurando a ajuda da tela. Padrão de `?` localizado de `.claude/03-code-conventions.md`.
 */
const helpScore: ComoFuncionaSecao[] = [
  {
    titulo: 'A conta',
    conteudo: (
      <div>
        <p>
          Cada proposta recebe uma nota de <strong>0 a 1</strong>, montada com os pesos desta cotação (mostrados à
          direita da legenda). Com preço 60% e prazo 40%, por exemplo:
        </p>
        <p>
          <strong>score = (60 × nota de preço + 40 × nota de prazo) ÷ 100</strong>
        </p>
        <p>
          A <strong>nota de preço</strong> é o menor preço do item dividido pelo preço da proposta — a mais barata
          tira 1,00, e uma que cobra o dobro tira 0,50. A <strong>nota de prazo</strong> segue a mesma ideia, com
          os dias somados de 1 para que a pronta entrega (zero dia) não divida por zero: pronta entrega tira 1,00,
          2 dias tira 0,33, 10 dias tira 0,09.
        </p>
      </div>
    ),
  },
  {
    titulo: 'Um exemplo',
    conteudo: (
      <div>
        <p>
          Item com propostas de R$ 950 (10 dias) e R$ 1.100 (pronta entrega), pesos 60/40. O menor preço é 950 e o
          menor prazo é 0.
        </p>
        <p>
          A de <strong>R$ 1.100</strong>: preço 950 ÷ 1.100 = 0,86; prazo 1 ÷ 1 = 1,00. Score = (60 × 0,86 + 40 ×
          1,00) ÷ 100 = <strong>0,92</strong>.
        </p>
        <p>
          A de <strong>R$ 950</strong>: preço 950 ÷ 950 = 1,00; prazo 1 ÷ 11 = 0,09. Score = (60 × 1,00 + 40 ×
          0,09) ÷ 100 = <strong>0,64</strong>.
        </p>
        <p>
          A mais barata perdeu no score por causa dos 10 dias — e continua marcada com <strong>[P]</strong>, porque
          menor preço é fato, não cálculo.
        </p>
      </div>
    ),
  },
  {
    titulo: 'Atraso pesa muito no começo, pouco no fim',
    conteudo: (
      <p>
        A nota de prazo cai rápido nos primeiros dias e vai achatando: de pronta entrega para 2 dias ela despenca
        de 1,00 para 0,33, mas de 10 para 13 dias muda quase nada. É proposital — o que muda o dia a dia da oficina
        é a diferença entre receber hoje e receber na semana que vem; entre o décimo e o décimo terceiro dia, o
        veículo já está parado de qualquer jeito.
      </p>
    ),
  },
  {
    titulo: 'Por que a comparação é por proporção',
    conteudo: (
      <p>
        As notas comparam cada proposta com <strong>a melhor daquele item</strong>, por divisão. A alternativa
        comum — esticar a melhor para 1 e a pior para 0 — engana quando há poucas propostas: com duas de R$ 1.000 e
        R$ 1.001, a segunda tiraria zero, como se fosse péssima. Por proporção ela tira 0,999, que é a verdade.
      </p>
    ),
  },
  {
    titulo: 'Qualidade e casos sem score',
    conteudo: (
      <p>
        Quando o preset dá peso à <strong>qualidade</strong>, entra também a nota do fornecedor (de 0 a 5).
        Fornecedor <strong>sem nota não é penalizado</strong>: aquele peso é redistribuído entre preço e prazo, e
        ele é avaliado só pelo que se sabe dele. E quando <strong>só um fornecedor</strong> cota o item, não aparece
        score — nota de candidato sozinho seria sempre 1,00 e não diria nada.
      </p>
    ),
  },
  {
    titulo: 'O score não decide',
    conteudo: (
      <p>
        Ele ordena a leitura, nada mais — a escolha é sua, item a item. E a governança está ancorada no{' '}
        <strong>menor preço</strong>, não no score: escolher acima do menor preço exige justificativa e aprovação do
        gestor <strong>mesmo quando o score aponta para aquela proposta</strong>. É o que impede que mudar os pesos
        vire um jeito de contornar a aprovação.
      </p>
    ),
  },
];

const helpCesta: ComoFuncionaSecao[] = [
  {
    titulo: 'O que acontece ao fechar',
    conteudo: (
      <p>
        O sistema gera <strong>um pedido por fornecedor vencedor</strong> no ERP, todos com a referência desta
        cotação. Se houver item acima do menor preço, nada é gerado antes da aprovação do gestor.
      </p>
    ),
  },
  {
    titulo: 'O pedido sai ligado à requisição',
    conteudo: (
      <p>
        Os pedidos entram no ERP ligados à <strong>requisição de origem</strong>, com o local de estoque, o
        centro de custo e a classificação financeira dela. Por isso, se a requisição não for encontrada no ERP,
        nenhum pedido é gerado. Quando o último pedido entra, a requisição é marcada como{' '}
        <strong>pedido gerado</strong> no ERP. Se só essa marcação falhar, os pedidos continuam valendo e o aviso
        diz para fazê-la na tela do ERP.
      </p>
    ),
  },
  {
    titulo: 'Sem gestor cadastrado, a cesta não fecha',
    conteudo: (
      <p>
        Quando há item <strong>acima do menor preço</strong>, alguém precisa aprovar — e quem aprova são os{' '}
        <strong>gestores de compras</strong>, cadastrados em Administração do Sistema, na aba Cotações. Se não
        houver nenhum cadastrado, o fechamento é <strong>recusado na hora</strong>, em vez de deixar a cotação
        parada esperando uma aprovação que ninguém pode dar. As duas saídas são pedir a um administrador que
        cadastre os gestores ou refazer as escolhas no <strong>menor preço</strong>, que dispensa aprovação.
      </p>
    ),
  },
  {
    titulo: 'Se um pedido falhar no ERP',
    conteudo: (
      <p>
        Os pedidos são enviados um a um, então parte pode entrar e parte falhar. O que falhou aparece com o
        motivo dado pelo ERP e o botão <strong>Reenviar</strong>. Antes de reenviar, o sistema confere se o
        pedido não chegou a entrar no ERP numa tentativa anterior, para não criar um segundo igual. Você também recebe um aviso no <strong>sino da tela de cotações</strong>{' '}
        — se fechar esta página no meio, o pendente não se perde de vista. Quando o último pedido entra, a cotação é
        concluída e o comprador é avisado, mesmo que quem tenha reenviado seja outra pessoa.
      </p>
    ),
  },
  {
    titulo: 'O frete entra no total, mas não no score',
    conteudo: (
      <div>
        <p>
          Cada fornecedor informa <strong>um frete por proposta</strong> — não por peça. Na revisão da cesta, o
          frete de cada fornecedor vencedor é somado <strong>uma vez</strong>, por mais itens que ele leve, e o
          total da cesta passa a ser o custo real da compra: peças + frete.
        </p>
        <p>
          O <strong>score de apoio continua ponderando só preço e prazo</strong>. Frete é custo da cesta, não do
          item: distribuí-lo item a item seria circular, porque dependeria de saber quem vence — que é justamente
          o que o score ajuda a decidir.
        </p>
        <p>
          Quando o frete de um fornecedor <strong>supera o valor das peças que ele venceu</strong>, a tela avisa:
          costuma compensar mover aquele item para outro fornecedor da cesta. E proposta enviada antes deste campo
          existir aparece como <strong>frete não informado</strong> — nesse caso o total não está fechado, e a tela
          diz isso.
        </p>
        <p>
          Por enquanto o frete <strong>não vai para o pedido do ERP</strong>: o pedido é gerado como &quot;sem
          frete&quot; e só com o valor das peças, até a modalidade de frete ser decidida.
        </p>
      </div>
    ),
  },
  {
    titulo: 'Gerar pedidos: a forma de pagamento',
    conteudo: (
      <p>
        Ao gerar os pedidos, você escolhe para cada fornecedor <strong>uma das formas de pagamento que ele marcou</strong>{' '}
        na proposta. É ela que vira a cobrança do pedido no Nasajon (forma, conta, parcelas e vencimento), conforme a
        configuração em Administração do Sistema → Cotações → Formas de pagamento. Forma ainda não configurada aparece
        desabilitada, e sem uma forma escolhida para cada pedido o botão fica bloqueado.
      </p>
    ),
  },
  {
    titulo: 'Itens sem compra',
    conteudo: (
      <p>
        Item sem nenhuma proposta não impede o fechamento: a cotação conclui <strong>parcial</strong> e o item fica
        registrado. Depois de concluída, o botão <strong>Recotar itens sem compra</strong> cria uma cotação nova, em
        rascunho, só com esses itens (com o mesmo veículo, chassi, local de entrega e foto); você escolhe os
        fornecedores nela antes de enviar.
      </p>
    ),
  },
  {
    titulo: 'Dois e-mails ao fornecedor',
    conteudo: (
      <div>
        <p>
          Depois de fechar a cesta, você pode pedir ao fornecedor o <strong>espelho da nota fiscal</strong> — a
          prévia com os itens que ele venceu e os valores, para conferir antes de o pedido entrar no ERP. Depois
          que o pedido entra, a linha dele oferece <strong>autorizar o faturamento</strong> por e-mail.
        </p>
        <p>
          Cada fornecedor recebe <strong>apenas os itens que venceu</strong>, nunca a cesta inteira. O texto dos
          dois vem de <strong>Administração do Sistema &rarr; Cotações &rarr; E-mails ao fornecedor</strong>.
        </p>
        <p>
          Cada envio fica registrado aqui, com data, quem enviou e para qual endereço. Se o e-mail não sair, a
          tela diz o motivo e <strong>não afirma que foi</strong> — e, quando não é possível verificar os envios
          anteriores, ela também diz isso, em vez de mostrar "nenhum envio ainda".
        </p>
      </div>
    ),
  },
];

/**
 * O texto padrão de quem seguiu o preset: só existe quando a proposta é a de melhor score e não é
 * a de menor preço. Fora disso, devolve vazio.
 *
 * Os dois prazos são COMPARADOS, então entram como rótulo ("pronta entrega", "10 dias") e não como
 * frase. ⚠️ Este texto vira a justificativa GRAVADA na auditoria: é o que alguém vai ler meses
 * depois para entender a compra.
 */
function justificativaDoPreset(presetNome: string, item: ItemComparado, proposta: PropostaComparada): string {
  if (!proposta.flags.melhorScore || proposta.flags.menorPreco) return '';
  const menorPreco = item.propostas.find((p) => p.flags.menorPreco);
  const delta = menorPreco ? proposta.totalItem - menorPreco.totalItem : 0;
  return `Seguiu o preset ${presetNome}: ${prazoTexto(proposta.prazoEntregaDias).toLowerCase()} contra ${prazoTexto(
    menorPreco?.prazoEntregaDias ?? 0,
  ).toLowerCase()} do menor preço; diferença de ${moeda(delta)}.`;
}

function moeda(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function dataHora(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

/** O prazo de respostas é só data desde a at-17, no fuso da oficina (o servidor grava o fim do dia). */
function dataDoPrazo(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

function prazoTexto(dias: number): string {
  return dias === 0 ? 'Pronta entrega' : `${dias} dia${dias > 1 ? 's' : ''}`;
}

/**
 * Prazo como FRASE (13/08/2026). `prazoTexto` devolve um rótulo ("Pronta entrega", "5
 * dias"), ótimo para célula de tabela e péssimo dentro de uma sentença: encaixá-lo em
 * "Entrega em até ..." produzia **"Entrega em até pronta entrega"**.
 *
 * Pronta entrega não é uma duração — é a ausência dela. Por isso a frase inteira muda, em
 * vez de a palavra ser interpolada num molde fixo.
 */
function prazoEntregaFrase(dias: number): string {
  return dias === 0 ? 'Pronta entrega' : `Entrega em até ${dias} dia${dias > 1 ? 's' : ''}`;
}

type Aba = 'painel' | 'comparacao' | 'cesta';

const PortalCotacaoDetalhe: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [cotacao, setCotacao] = useState<CotacaoDetalhe | null>(null);
  const [comparacao, setComparacao] = useState<Comparacao | null>(null);
  const [pedidos, setPedidos] = useState<PedidoGerado[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [aba, setAba] = useState<Aba>('painel');
  const [ajuda, setAjuda] = useState<Aba | null>(null);
  // Estado PRÓPRIO do `?` da legenda do score — o `ajuda` acima é o drawer da aba.
  const [ajudaScoreAberta, setAjudaScoreAberta] = useState(false);
  const [processando, setProcessando] = useState(false);

  /**
   * Estado da integração de mensagens (at-07) — decide QUAL botão de WhatsApp a linha do
   * fornecedor oferece: o envio pelo servidor ou o `wa.me` de sempre.
   *
   * ⚠️ Começa `null` e a falha da consulta é **fail-open**: sem resposta, a tela mostra o
   * caminho manual, que funciona sempre. O inverso — assumir envio automático — prometeria
   * um disparo que ninguém faria. Mesmo princípio do fail-open das pausas (at-48b): a
   * imposição real é do backend, que recusa o envio ativo quando a integração está fora.
   */
  const [octadesk, setOctadesk] = useState<StatusOctadesk | null>(null);

  // Decisões em edição na matriz (chave = cotacaoItemId).
  const [escolhas, setEscolhas] = useState<Record<string, string | null>>({});
  const [justificativas, setJustificativas] = useState<Record<string, string>>({});
  const [modalJustificativa, setModalJustificativa] = useState<{
    item: ItemComparado;
    proposta: PropostaComparada;
    menorPreco: PropostaComparada | undefined;
  } | null>(null);
  const [textoJustificativa, setTextoJustificativa] = useState('');
  // Itens que vão salvar com a justificativa padrão do preset: confirmados antes do envio.
  const [confirmarRascunho, setConfirmarRascunho] = useState<{
    apenas?: string;
    itens: { id: string; descricao: string; texto: string }[];
  } | null>(null);

  const [confirmacao, setConfirmacao] = useState<
    null | 'enviar' | 'encerrar' | 'cancelar' | 'fechar-cesta' | 'gerar-pedidos' | 'reabrir' | 'recotar'
  >(null);
  /** O novo prazo ao reabrir para respostas (at-18): só a data, como na criação. */
  const [novoPrazo, setNovoPrazo] = useState('');
  const [motivoCancelamento, setMotivoCancelamento] = useState('');
  const [estadoDaFoto, setEstadoDaFoto] = useState<EstadoDaFotoDoVeiculo | null>(null);
  // A matriz de comparação rola arrastando com o mouse e pelas setas da legenda (at-16).
  const matriz = useArrastarParaRolar<HTMLDivElement>();
  const [pedidoDeEscolhaDaFoto, setPedidoDeEscolhaDaFoto] = useState(0);

  /**
   * Resultado do último disparo de convite (12/08/2026): "Enviar convites" e as ações do modal
   * do convite ("Reenviar por e-mail", "Gerar novo link") abrem este modal com os FATOS —
   * canal, destinatário, falha e o link novo em claro — em vez do toast fixo que dizia
   * "enviado" com o e-mail falhado (SMTP fora do ar).
   */
  const [resultadoDisparo, setResultadoDisparo] = useState<{
    titulo: string;
    linhas: LinhaDisparo[];
    link?: string;
    linkMudou?: boolean;
    mensagem?: string;
    expiraEm?: string;
  } | null>(null);

  /**
   * Convite cuja proposta está aberta no modal (12/08/2026): o nome do fornecedor que
   * respondeu é clicável — padrão da casa, o nome é o link. Guarda o ID, não o objeto:
   * o `carregar()` troca o `cotacao` inteiro e um objeto guardado ficaria defasado.
   */
  const [propostaAbertaId, setPropostaAbertaId] = useState<string | null>(null);

  /**
   * Convite aberto no modal de ações (at-07). Guarda o ID, não o objeto: o `carregar()` troca o
   * `cotacao` inteiro depois de cada ação, e um objeto guardado ficaria defasado — mesmo motivo
   * do `propostaAbertaId` acima.
   */
  const [conviteAberto, setConviteAberto] = useState<string | null>(null);

  /**
   * Os dois e-mails ao fornecedor depois da decisão (at-11): o último envio de cada par
   * (fornecedor, finalidade).
   *
   * ⚠️ `erroEmails` existe porque "nenhum envio" e "não consegui verificar" levam a ações
   * opostas — o primeiro convida a enviar, o segundo a não enviar de novo (regra da at-60).
   * Sem a distinção, uma falha de carga faria o comprador reenviar um e-mail que já saiu.
   */
  const [emailsEnviados, setEmailsEnviados] = useState<EmailFornecedorEnviado[]>([]);
  const [erroEmails, setErroEmails] = useState(false);
  const [confirmacaoEmail, setConfirmacaoEmail] = useState<{
    finalidade: FinalidadeEmailFornecedor;
    fornecedorCnpj: string;
    fornecedorNome: string;
    pedidoId?: string;
  } | null>(null);

  const carregarEmails = useCallback(async () => {
    if (!id) return;
    try {
      setEmailsEnviados(await fetchEmailsFornecedor(id));
      setErroEmails(false);
    } catch (error) {
      setEmailsEnviados([]);
      setErroEmails(true);
      notify.warning(
        'Não foi possível verificar os e-mails já enviados ao fornecedor. Confira antes de enviar de novo.',
        { toastId: 'emails-fornecedor-indisponivel' },
      );
    }
  }, [id]);

  /** Quando a tela foi atualizada pela última vez com sucesso (at-17, botão "Atualizar"). */
  const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);
  /** Modal de convidar fornecedor depois do envio (at-17). */
  const [convidando, setConvidando] = useState(false);
  /** Os itens já marcados ao abrir o convite: os que estão sem oferta, quando vem da faixa (at-18). */
  const [itensDoConvite, setItensDoConvite] = useState<string[] | undefined>(undefined);
  const convidar = (cotacaoItemIds?: string[]) => {
    setItensDoConvite(cotacaoItemIds);
    setConvidando(true);
  };
  /** Convite cujo histórico de correções está aberto (at-17). */
  const [historicoConviteId, setHistoricoConviteId] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    if (!id) return;
    setCarregando(true);
    try {
      const detalhe = await fetchCotacao(id);
      setCotacao(detalhe);
      setAtualizadoEm(new Date());
      if (['EM_DECISAO', 'AGUARDANDO_APROVACAO', 'GERANDO_PEDIDOS', 'CONCLUIDA'].includes(detalhe.status)) {
        const comp = await fetchComparacao(id);
        setComparacao(comp);
        const iniciais: Record<string, string | null> = {};
        const justIniciais: Record<string, string> = {};
        comp.itens.forEach((item) => {
          if (item.decisao?.propostaItemVencedorId) {
            iniciais[item.cotacaoItemId] = item.decisao.propostaItemVencedorId;
            if (item.decisao.justificativa) justIniciais[item.cotacaoItemId] = item.decisao.justificativa;
          } else if (item.bloqueado || item.aprovacao?.status === 'REPROVADA') {
            /*
             * Sem pré-seleção aqui (13/08/2026): no item REPROVADO, o melhor score é —
             * muito provavelmente — a mesma proposta que o gestor acabou de rejeitar;
             * pré-marcá-la escondia o aviso de reprovação e convidava a reenviar a mesma
             * escolha. E item bloqueado sem vencedor (fechado como sem-compra) não pode
             * exibir uma célula marcada que não corresponde a decisão nenhuma.
             */
            iniciais[item.cotacaoItemId] = null;
          } else {
            const melhor = item.propostas.find((p) => p.flags.melhorScore);
            iniciais[item.cotacaoItemId] = melhor?.propostaItemId ?? null;
          }
        });
        setEscolhas(iniciais);
        setJustificativas(justIniciais);
      }
      if (['GERANDO_PEDIDOS', 'CONCLUIDA'].includes(detalhe.status)) {
        setPedidos(await fetchPedidos(id));
        /*
         * Fora do `try` da tela (at-11): a falha em verificar os e-mails já enviados é um
         * aviso, não motivo para a cotação inteira sumir. O `carregarEmails` tem catch
         * próprio e nunca propaga.
         */
        await carregarEmails();
      }
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [id, carregarEmails]);

  /*
   * Estado da integração de mensagens (at-07). Consulta própria, uma vez na montagem: não é
   * dado da cotação, e refazê-la a cada `carregar()` (que roda depois de toda ação) só
   * gastaria requisição.
   *
   * A falha NÃO bloqueia a tela — só avisa e mantém o caminho manual. `toastId` é obrigatório
   * aqui: sem ele, o aviso repetiria a cada remontagem da tela.
   */
  useEffect(() => {
    let ativo = true;
    obterStatusOctadesk()
      .then((s) => {
        if (ativo) setOctadesk(s);
      })
      .catch(() => {
        if (!ativo) return;
        notify.warning(
          'Não foi possível verificar se o envio automático de WhatsApp está ativo. O envio manual continua disponível.',
          { toastId: 'octadesk-status-indisponivel' },
        );
      });
    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /*
   * Painel ao vivo (ADR 0005, fase B): entra na sala desta cotação e recarrega quando o
   * fornecedor se mexe — abriu o link, visualizou, respondeu, reeditou, declinou — ou
   * quando o cron encerra o prazo. É o que faz "2 de 3 responderam" mudar sem F5.
   *
   * ⚠️ **Debounce**: abrir o link gera LINK_ACESSADO e COTACAO_VISUALIZADA em sequência,
   * com milissegundos de diferença. Sem ele seriam dois `carregar()` seguidos, e a tela
   * piscaria duas vezes para um único acontecimento.
   *
   * ⚠️ Confere o `cotacaoId` do evento mesmo estando numa sala por cotação: defesa em
   * profundidade barata contra recarregar por movimento de outra cotação se a sala mudar
   * no futuro.
   */
  useEffect(() => {
    if (!id) return;
    const socket = obterPortalSocket();
    if (!socket) return;

    socket.emit('cotacao:entrar', { cotacaoId: id });

    let debounce: ReturnType<typeof setTimeout> | null = null;
    const aoAtualizar = (detail: { cotacaoId?: string }) => {
      if (detail?.cotacaoId !== id) return;
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => carregar(), 2000);
    };

    socket.on('cotacao:atualizada', aoAtualizar);

    return () => {
      if (debounce) clearTimeout(debounce);
      socket.off('cotacao:atualizada', aoAtualizar);
      socket.emit('cotacao:sair', { cotacaoId: id });
    };
  }, [id, carregar]);

  const naoResponderam = useMemo(
    () =>
      (cotacao?.convites ?? [])
        .filter((c) => !['RESPONDIDO', 'DECLINADO', 'REVOGADO'].includes(c.status))
        .map((c) => c.fornecedor.razaoSocial),
    [cotacao],
  );

  const responderam = (cotacao?.convites ?? []).filter((c) => c.status === 'RESPONDIDO').length;
  const convidados = (cotacao?.convites ?? []).filter((c) => c.status !== 'REVOGADO').length;

  /**
   * Os e-mails ao fornecedor (at-11) só existem depois de a cesta fechar e ser aprovada.
   *
   * ⚠️ `CONCLUIDA` entra: o espelho costuma chegar depois de o pedido já estar no ERP, e a
   * autorização de faturamento acontece **sempre** depois. Parar em `GERANDO_PEDIDOS` tiraria
   * os dois botões exatamente quando eles são usados.
   */
  const podeEmailFornecedor = ['GERANDO_PEDIDOS', 'CONCLUIDA'].includes(cotacao?.status ?? '');

  /**
   * Quem anexa e remove imagem (at-11) — os mesmos papéis do `@Roles` das rotas.
   *
   * O TI enxerga a cotação para dar suporte e **não opera** (ADR 0012): sem este gating a tela
   * ofereceria a ele um botão que só devolve 403 (regra permanente da at-25).
   */
  const podeAnexar = useAlcanca(['COMPRA', 'ADMIN']);

  // ── Ações do painel ────────────────────────────────────────────────────────

  async function executar(acao: () => Promise<unknown>, sucesso: string) {
    setProcessando(true);
    try {
      await acao();
      toast.success(sucesso);
      setConfirmacao(null);
      await carregar();
    } catch (error) {
      /*
       * `notify.errorFrom` porque aqui o MOTIVO do backend é a informação útil — é a regra
       * de erro de AÇÃO da at-65 (o backend explica o que impediu), oposta à dos avisos
       * sobre estado enganoso da tela, que usam texto fixo.
       *
       * O caso que tornou isto visível é o `SEM_GESTOR_CADASTRADO` do "Fechar cesta"
       * (at-06): a mensagem carrega as duas saídas — cadastrar gestor ou refazer as
       * escolhas no menor preço — e nenhum texto genérico daqui saberia dizer isso.
       * O `catch` também é o que garante que o toast de sucesso NÃO saia numa ação que
       * falhou (falso sucesso, at-60).
       */
      notify.errorFrom(error, 'Não foi possível concluir a ação.');
    } finally {
      setProcessando(false);
    }
  }

  // ── E-mails ao fornecedor depois da decisão (at-11) ────────────────────────

  /** O último envio daquele par, ou `undefined` quando ainda não houve nenhum. */
  function ultimoEmail(cnpj: string, finalidade: FinalidadeEmailFornecedor) {
    return emailsEnviados.find((e) => e.fornecedorCnpj === cnpj && e.finalidade === finalidade);
  }

  /**
   * A linha "último envio" que acompanha cada botão.
   *
   * 🔴 Três estados, e eles **não podem se parecer**: não consegui verificar (não envie de
   * novo às cegas), nunca enviei (pode enviar) e enviei mas falhou (o motivo está aqui).
   * Colapsar o primeiro nos outros é o defeito da at-60 — e aqui ele custa um e-mail duplicado
   * ao fornecedor, ou um e-mail que ninguém percebe que nunca saiu.
   */
  function linhaUltimoEnvio(envio: EmailFornecedorEnviado | undefined) {
    if (erroEmails) {
      return (
        <span className="pc-texto-secundario">
          Não foi possível verificar os envios anteriores — confira antes de enviar de novo.
        </span>
      );
    }
    if (!envio) return <span className="pc-texto-secundario">Nenhum envio ainda.</span>;

    const quando = new Date(envio.em).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    return (
      <span className="pc-texto-secundario">
        Último envio: {quando}
        {envio.por ? ` por ${envio.por}` : ''}, para {envio.destinatario} —{' '}
        {envio.sucesso ? (
          'enviado'
        ) : (
          <strong style={{ color: 'var(--tema-texto-vermelho, #b42318)' }}>não saiu: {envio.erro ?? 'motivo não informado'}</strong>
        )}
      </span>
    );
  }

  /**
   * Prévia do destinatário, para o modal de confirmação.
   *
   * ⚠️ É **prévia**: quem escolhe o contato de verdade é o backend (`escolherContato`), e o
   * endereço que saiu volta na resposta — é ele que aparece no aviso depois do envio. Esta
   * função existe só para o comprador conferir antes de disparar, e devolve `null` quando não
   * sabe, em vez de arriscar um palpite.
   */
  function emailPrevisto(cnpj: string): string | null {
    const contatos = cotacao?.convites.find((c) => c.fornecedorCnpj === cnpj)?.fornecedor.contatos ?? [];
    return contatos.find((c) => c.principal && c.email)?.email ?? contatos.find((c) => c.email)?.email ?? null;
  }

  /**
   * Dispara um dos dois e-mails.
   *
   * 🔴 **Não usa o `executar`**: aquele emite o toast de sucesso pela simples ausência de
   * exceção, e aqui o servidor responde **200 com `sucesso: false`** quando o SMTP recusa — o
   * envio falhou, o registro ficou gravado, e dizer "enviado" seria o falso sucesso da at-60
   * com consequência externa (o comprador esperaria uma resposta que nunca viria).
   */
  async function dispararEmail(acao: () => Promise<EnvioDeEmail>, rotulo: string) {
    setProcessando(true);
    try {
      const resposta = await acao();
      if (resposta.sucesso) {
        notify.success(`${rotulo} enviado para ${resposta.destinatario}.`);
      } else {
        notify.error(
          `${rotulo} NÃO foi enviado para ${resposta.destinatario}: ${resposta.erro ?? 'motivo não informado'}. ` +
            'A tentativa ficou registrada; corrija o endereço ou tente de novo.',
        );
      }
      await carregarEmails();
    } catch (error) {
      notify.errorFrom(error, `Não foi possível enviar o ${rotulo.toLowerCase()}.`);
    } finally {
      setProcessando(false);
      setConfirmacaoEmail(null);
    }
  }

  /**
   * Envio ATIVO por WhatsApp (at-07): quem envia é o servidor, pelo número oficial da empresa.
   *
   * Duas coisas que este fluxo faz de propósito e o manual não:
   * - **não abre nada** — não há mensagem para a pessoa despachar;
   * - **não avisa de link novo**, porque reaproveita o vigente (só avisa no caso raro em que o
   *   token era irrecuperável e precisou ser trocado).
   *
   * ⚠️ **Em falha, NÃO oferece o botão manual** (mudou na at-08, 20/08/2026): desde que o
   * WhatsApp virou exclusivo por estado da integração (`ConviteAcoesModal`), não existe mais um
   * "WhatsApp manual" escondido ao lado para oferecer — ele só aparece quando a integração está
   * desligada. O texto do Victor orienta a saída real: pedir à TI para desligar a integração
   * (é ADMIN/TI que acessa Administração do Sistema → Integrações; quem opera cotação é
   * COMPRA, e não tem esse acesso). Continua valendo a decisão do ADR 0006 de não haver
   * fallback automático — a pessoa decide, só que agora pela integração, não por um botão.
   */
  async function enviarWhatsappPeloServidor(conviteId: string) {
    setProcessando(true);
    try {
      const r = await enviarWhatsappConvite(conviteId);
      if (r.ok) {
        // Texto à mão: `actionSuccess` conjuga o verbo no masculino singular e produziria
        // frase errada aqui ("Convite enviado concluído com sucesso").
        notify.success(
          r.linkMudou
            ? `Convite enviado por WhatsApp para ${nomeDoConvite(conviteId)}. A mensagem leva um link novo; o anterior deixou de valer.`
            : `Convite enviado por WhatsApp para ${nomeDoConvite(conviteId)}.`,
        );
      } else {
        // A plataforma recusou. O motivo é o dela (já sanitizado no backend), seguido da
        // orientação exata pedida pelo Victor (20/08/2026) — texto usado como está.
        notify.error(
          `${r.resultado.erro ?? 'A plataforma de mensagens não enviou o convite.'} Envio pelo Octa falhou. ` +
            'Se persistir, solicite ao TI a desativação da integração com o Octa em Administração do Sistema → ' +
            'Integrações para voltar ao envio manual',
        );
      }
      await carregar();
    } catch (error) {
      notify.actionError(error, { action: 'send', subject: 'convite por WhatsApp' });
    } finally {
      setProcessando(false);
    }
  }

  async function abrirWhatsapp(conviteId: string) {
    try {
      const { url } = await obterLinkWhatsapp(conviteId);
      window.open(url, '_blank', 'noopener');
      // O aviso do link novo é obrigatório: este botão também ROTACIONA o token, e até
      // 12/08/2026 fazia isso em silêncio — o fornecedor com o link antigo ficava fora.
      toast.info(
        'Mensagem pronta no WhatsApp — o envio é feito por você. A mensagem leva um link novo; o enviado antes deixou de valer.',
      );
      await carregar();
    } catch (error) {
      toast.error((error as Error).message);
    }
  }

  // ── Disparo de convites com resultado VISÍVEL (12/08/2026) ─────────────────
  //
  // Os três caminhos abaixo substituem o `executar(...)` + toast fixo: o backend devolve o
  // desfecho por canal e o link novo, e é isso que o usuário vê. Regra da at-60: e-mail
  // falhado NUNCA pode sair como "enviado".

  function nomeDoConvite(conviteId: string): string {
    const convite = cotacao?.convites.find((c) => c.id === conviteId);
    return convite?.fornecedor.razaoSocial ?? 'Fornecedor';
  }

  function houveFalha(resultados: ResultadoDisparo[]): boolean {
    // `manual` (WhatsApp) não é falha nem sucesso automático — não conta aqui.
    return resultados.some((r) => !r.manual && !r.sucesso);
  }

  async function enviarConvites() {
    if (!cotacao) return;
    setProcessando(true);
    try {
      const r = await enviarCotacao(cotacao.id);
      setConfirmacao(null);
      const linhas: LinhaDisparo[] = r.disparos.map((d) => ({
        fornecedorNome: nomeDoConvite(d.conviteId),
        resultados: d.resultados,
      }));
      const falhou = linhas.some((l) => houveFalha(l.resultados));
      if (falhou) {
        // A cotação FOI enviada (status mudou); o que falhou foi canal — warning, não error,
        // e nada de toast de sucesso (padrão "refresh pós-ação" da at-60, adaptado).
        notify.warning('Cotação enviada, mas nem todo convite saiu — veja o detalhe por fornecedor.');
      } else {
        notify.success('Convites enviados.');
      }
      setResultadoDisparo({ titulo: 'Resultado do envio', linhas });
      await carregar();
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível enviar os convites.');
    } finally {
      setProcessando(false);
    }
  }

  /*
   * A ação "Ver link" (12/08/2026) deixou de existir na at-07: o link vigente passou a ser
   * CARREGADO pelo `ConviteAcoesModal` na abertura, com botão de copiar. A leitura sempre foi
   * pura (ADR 0004), então exibi-la de entrada é melhor que ter uma ação para pedir a
   * informação. O `obterLinkVigente` agora é chamado de lá.
   */

  async function reenviar(conviteId: string) {
    setProcessando(true);
    try {
      const r = await reenviarConvite(conviteId);
      const linhas = [{ fornecedorNome: nomeDoConvite(conviteId), resultados: r.resultados }];
      if (houveFalha(r.resultados)) {
        notify.warning('O reenvio não saiu por todos os canais — o link está no detalhe, entregue por outro meio.');
      }
      setResultadoDisparo({
        titulo: 'Convite reenviado',
        linhas,
        link: r.link,
        // O reenvio normal reaproveita o link vigente; só rotaciona em convite antigo, e
        // aí o aviso precisa ser o oposto. Quem decide é o backend, não a tela.
        linkMudou: r.linkMudou ?? true,
      });
      await carregar();
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível reenviar o convite.');
    } finally {
      setProcessando(false);
    }
  }

  async function gerarNovoLink(conviteId: string) {
    setProcessando(true);
    try {
      const r = await rotacionarConvite(conviteId);
      const linhas = [{ fornecedorNome: nomeDoConvite(conviteId), resultados: r.resultados }];
      // `linkMudou` fica no default `true`: rotacionar é exatamente o que este botão faz.
      setResultadoDisparo({ titulo: 'Link novo gerado', linhas, link: r.link });
      await carregar();
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível gerar um link novo.');
    } finally {
      setProcessando(false);
    }
  }

  // ── Matriz de comparação ───────────────────────────────────────────────────

  function selecionarProposta(item: ItemComparado, proposta: PropostaComparada) {
    if (proposta.flags.menorPreco) {
      setEscolhas((e) => ({ ...e, [item.cotacaoItemId]: proposta.propostaItemId }));
      setJustificativas((j) => {
        const novo = { ...j };
        delete novo[item.cotacaoItemId];
        return novo;
      });
      return;
    }
    // Fora do menor preço: a justificativa é coletada AGORA, com o contexto
    // fresco — não no fim, quando o comprador já não lembra o porquê.
    const menorPreco = item.propostas.find((p) => p.flags.menorPreco);
    setTextoJustificativa(justificativas[item.cotacaoItemId] ?? justificativaDoPreset(comparacao?.presetNome ?? '', item, proposta));
    setModalJustificativa({ item, proposta, menorPreco });
  }

  /**
   * A justificativa que vale para o item: a escrita pelo comprador ou, se ele não escreveu e a
   * escolha é a do preset, o texto padrão do preset (decisão do Victor, 28/10/2026 — substitui a
   * regra de 13/08/2026 de nunca preencher sozinho). Escolha fora do preset continua exigindo
   * texto do comprador.
   */
  const presetNome = comparacao?.presetNome ?? '';
  const justificativaEfetiva = useCallback(
    (item: ItemComparado): string => {
      const escrita = (justificativas[item.cotacaoItemId] ?? '').trim();
      if (escrita) return escrita;
      const proposta = item.propostas.find((p) => p.propostaItemId === escolhas[item.cotacaoItemId]);
      return proposta ? justificativaDoPreset(presetNome, item, proposta) : '';
    },
    [justificativas, escolhas, presetNome],
  );

  function confirmarJustificativa() {
    if (!modalJustificativa) return;
    const { item, proposta } = modalJustificativa;
    setEscolhas((e) => ({ ...e, [item.cotacaoItemId]: proposta.propostaItemId }));
    setJustificativas((j) => ({ ...j, [item.cotacaoItemId]: textoJustificativa.trim() }));
    setModalJustificativa(null);
  }

  const totaisCesta = useMemo(() => {
    if (!comparacao)
      return { escolhido: 0, menorPreco: 0, divergentes: 0, semCompra: 0, semJustificativa: [] as string[] };
    let escolhido = 0;
    let menorPreco = 0;
    let divergentes = 0;
    let semCompra = 0;
    /*
     * Itens divergentes AINDA SEM JUSTIFICATIVA (13/08/2026).
     *
     * A tela pré-seleciona a proposta de melhor score quando não há decisão salva — e essa
     * proposta pode não ser o menor preço, como no caso que o Victor viu. A pré-seleção não
     * passa pelo modal que coleta a justificativa, então o item nascia divergente e sem
     * texto, com o botão de salvar habilitado: o comprador só descobriria no 400 do backend
     * ("Justifique a escolha do item ..."), que já barra isso corretamente.
     *
     * Desde 28/10/2026 (decisão do Victor) a escolha do PRESET leva o texto padrão dele
     * (`justificativaEfetiva`); só a escolha fora do preset ainda exige texto do comprador.
     */
    const semJustificativa: string[] = [];
    comparacao.itens.forEach((item) => {
      const mp = item.propostas.find((p) => p.flags.menorPreco);
      if (mp) menorPreco += mp.totalItem;
      const escolhaId = escolhas[item.cotacaoItemId];
      if (!escolhaId) {
        if (item.propostas.length === 0) semCompra += 1;
        return;
      }
      const p = item.propostas.find((x) => x.propostaItemId === escolhaId);
      if (!p) return;
      escolhido += p.totalItem;
      if (!p.flags.menorPreco) {
        divergentes += 1;
        if (!justificativaEfetiva(item)) semJustificativa.push(item.descricao);
      }
    });
    return { escolhido, menorPreco, divergentes, semCompra, semJustificativa };
  }, [comparacao, escolhas, justificativaEfetiva]);

  /** A decisão de UM item no formato que o backend espera. */
  function decisaoDoItem(item: ItemComparado): DecisaoPayload {
    const escolhaId = escolhas[item.cotacaoItemId];
    if (!escolhaId) {
      return {
        cotacaoItemId: item.cotacaoItemId,
        motivoSemCompra: item.propostas.length === 0 ? ('SEM_PROPOSTA' as const) : ('DESCARTADO' as const),
      };
    }
    return {
      cotacaoItemId: item.cotacaoItemId,
      propostaItemVencedorId: escolhaId,
      justificativa: justificativaEfetiva(item) || undefined,
    };
  }

  /*
   * ESTADO DE RASCUNHO POR ITEM (13/08/2026 — pedido do Victor).
   *
   * O trabalho da comparação (escolher e justificar item a item) vivia só em estado local:
   * sair da tela ou recarregar perdia tudo o que não tinha passado por "Salvar e revisar
   * cesta". O backend, no entanto, **já salva parcial** — o `salvarDecisoes` percorre apenas
   * os itens enviados e faz upsert por item —, e a carga da tela já relê as decisões salvas.
   * Só faltava a tela oferecer o gesto.
   *
   * Cada item fica em um de três estados, comparando o local com o que veio do servidor:
   *  - `salvo`: igual ao que está gravado;
   *  - `alterado`: existe escolha local diferente da gravada (inclui a pré-seleção de melhor
   *    score, que é sugestão da tela e não decisão de ninguém até ser salva);
   *  - `bloqueado`: alterado, mas acima do menor preço SEM justificativa — o backend recusa
   *    (`JUSTIFICATIVA_OBRIGATORIA`), então não adianta enviar.
   */
  const estadoRascunho = useMemo(() => {
    const porItem = new Map<string, 'salvo' | 'alterado' | 'bloqueado' | 'reaberto'>();
    if (!comparacao) return porItem;

    comparacao.itens.forEach((item) => {
      /*
       * Item liquidado (13/08/2026): aprovado pelo gestor, aguardando o gestor, ou fechado
       * sem divergência depois de a cesta já ter ido ao gestor. Fica FORA do fluxo de
       * rascunho — o backend recusa alteração nele (`ITEM_BLOQUEADO_POS_FECHAMENTO`), e
       * incluí-lo em `alterados` faria o salvar em lote derrubar o lote inteiro.
       */
      if (item.bloqueado) return;

      const escolhaLocal = escolhas[item.cotacaoItemId] ?? null;
      const justLocal = justificativaEfetiva(item);
      const escolhaSalva = item.decisao?.propostaItemVencedorId ?? null;
      const justSalva = (item.decisao?.justificativa ?? '').trim();

      /*
       * Item REPROVADO pelo gestor e ainda sem nova escolha (13/08/2026). Não pode cair em
       * 'salvo' (a decisão dele foi ZERADA pela reprovação — a linha existe, mas não diz
       * nada) nem em 'alterado' (o salvar em lote inferiria `DESCARTADO` para item sem
       * escolha, e o item reprovado sairia da compra em silêncio — o gestor reprovou a
       * ESCOLHA, não a compra). Estado próprio: exige uma decisão ativa do comprador.
       */
      if (item.aprovacao?.status === 'REPROVADA' && !escolhaLocal && !escolhaSalva) {
        porItem.set(item.cotacaoItemId, 'reaberto');
        return;
      }

      if (escolhaLocal === escolhaSalva && justLocal === justSalva) {
        if (item.decisao) {
          porItem.set(item.cotacaoItemId, 'salvo');
        } else {
          /*
           * Nunca decidido e sem escolha local — o caso do item que NINGUÉM cotou. Ele
           * também precisa de decisão gravada (`SEM_PROPOSTA`), senão o "Fechar cesta"
           * cai em `DECISOES_INCOMPLETAS` e não há caminho na tela para resolver: antes,
           * quem gravava isso era o "Salvar e revisar cesta", que enviava todos os itens.
           */
          porItem.set(item.cotacaoItemId, 'alterado');
        }
        return;
      }

      const proposta = item.propostas.find((p) => p.propostaItemId === escolhaLocal);
      const divergenteSemTexto = proposta && !proposta.flags.menorPreco && !justLocal;
      porItem.set(item.cotacaoItemId, divergenteSemTexto ? 'bloqueado' : 'alterado');
    });

    return porItem;
  }, [comparacao, escolhas, justificativaEfetiva]);

  /*
   * `forEach` do Map em vez de espalhar `entries()`: o alvo de compilação do CRA aqui não
   * permite iterar `IterableIterator` sem `downlevelIteration` (erro TS2802).
   */
  const porEstado = useMemo(() => {
    const alterados: string[] = [];
    const bloqueados: string[] = [];
    const reabertos: string[] = [];
    estadoRascunho.forEach((estado, id) => {
      if (estado === 'alterado') alterados.push(id);
      if (estado === 'bloqueado') bloqueados.push(id);
      if (estado === 'reaberto') reabertos.push(id);
    });
    return { alterados, bloqueados, reabertos };
  }, [estadoRascunho]);
  const itensAlterados = porEstado.alterados;
  const itensBloqueados = porEstado.bloqueados;
  // Reprovados pelo gestor ainda sem nova escolha — travam a cesta até serem decididos.
  const itensReabertos = porEstado.reabertos;

  /**
   * Salva o que está pronto, SEM avançar para a cesta. `apenas` limita a um item (botão da
   * linha); sem ele, salva todos os alterados.
   *
   * ⚠️ Itens bloqueados são deixados de fora **e o toast diz isso**: enviar derrubaria o
   * lote inteiro no `JUSTIFICATIVA_OBRIGATORIA`, e omitir em silêncio faria o comprador
   * acreditar que guardou um trabalho que não guardou (a classe de defeito do at-60).
   */
  /**
   * Antes de salvar, avisa quais itens vão com a justificativa PADRÃO do preset (pedido do
   * Victor, 28/10/2026): o texto vai para a auditoria e para o gestor, então o comprador vê o
   * que está assinando. Sem item nessa situação, salva direto.
   */
  function pedirSalvarRascunho(apenas?: string) {
    if (!comparacao) return;
    const alvos = apenas ? [apenas] : itensAlterados;
    const automaticos = comparacao.itens
      .filter((i) => alvos.includes(i.cotacaoItemId) && !(justificativas[i.cotacaoItemId] ?? '').trim())
      .map((i) => ({ id: i.cotacaoItemId, descricao: i.descricao, texto: justificativaEfetiva(i) }))
      .filter((x) => x.texto);
    if (automaticos.length === 0) {
      void salvarRascunho(apenas);
      return;
    }
    setConfirmarRascunho({ apenas, itens: automaticos });
  }

  async function salvarRascunho(apenas?: string) {
    if (!comparacao) return;
    const alvos = apenas ? [apenas] : itensAlterados;
    if (alvos.length === 0) return;

    const payload: DecisaoPayload[] = comparacao.itens
      .filter((i) => alvos.includes(i.cotacaoItemId))
      .map(decisaoDoItem);

    setProcessando(true);
    try {
      const atualizada = await salvarDecisoes(comparacao.cotacaoId, payload);
      setComparacao(atualizada);
      const bloqueadosAgora = apenas ? [] : itensBloqueados;
      if (bloqueadosAgora.length > 0) {
        toast.warn(
          `Rascunho salvo: ${payload.length} ${payload.length === 1 ? 'item' : 'itens'}. ` +
            `${bloqueadosAgora.length} ${bloqueadosAgora.length === 1 ? 'ficou de fora por falta de justificativa' : 'ficaram de fora por falta de justificativa'}.`,
        );
      } else {
        toast.success(`Rascunho salvo: ${payload.length} ${payload.length === 1 ? 'item' : 'itens'}.`);
      }
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setProcessando(false);
    }
  }

  /*
   * "Salvar e revisar cesta" foi REMOVIDO em 13/08/2026 (decisão do Victor): o salvamento
   * passou a ser sempre pelo rascunho, e este botão só navega.
   *
   * ⚠️ Ele exige que **tudo esteja salvo**, e não é rigor gratuito: a aba da cesta é montada
   * a partir do estado LOCAL (`cestaPorFornecedor` lê `escolhas`), então revisar com escolha
   * não gravada mostraria uma cesta que o servidor desconhece — e o "Fechar cesta" logo
   * adiante recusaria com `DECISOES_INCOMPLETAS`, no pior momento possível.
   */
  const cestaPronta =
    itensAlterados.length === 0 && itensBloqueados.length === 0 && itensReabertos.length === 0;

  // Agrupamento da cesta por fornecedor vencedor.
  const cestaPorFornecedor = useMemo(() => {
    if (!comparacao) return [];
    const mapa = new Map<
      string,
      { nome: string; itens: { descricao: string; total: number; divergente: boolean }[]; subtotal: number; maiorPrazo: number }
    >();
    comparacao.itens.forEach((item) => {
      const escolhaId = escolhas[item.cotacaoItemId];
      if (!escolhaId) return;
      const p = item.propostas.find((x) => x.propostaItemId === escolhaId);
      if (!p) return;
      const atual = mapa.get(p.fornecedorCnpj) ?? {
        nome: p.fornecedorNome,
        itens: [],
        subtotal: 0,
        maiorPrazo: 0,
      };
      atual.itens.push({ descricao: item.descricao, total: p.totalItem, divergente: !p.flags.menorPreco });
      atual.subtotal += p.totalItem;
      atual.maiorPrazo = Math.max(atual.maiorPrazo, p.prazoEntregaDias);
      mapa.set(p.fornecedorCnpj, atual);
    });
    return Array.from(mapa.entries()).map(([cnpj, dados]) => {
      /*
       * O FRETE (at-11) vem do backend como atributo da proposta daquele fornecedor —
       * `porFornecedor[].frete` —, e entra aqui UMA vez, por mais itens que ele leve: é
       * frete de proposta, não de item. `null` = proposta anterior ao campo, e a tela diz
       * "não informado" em vez de somar 0.
       */
      const frete = comparacao.porFornecedor.find((f) => f.fornecedorCnpj === cnpj)?.frete ?? null;
      return {
        cnpj,
        ...dados,
        frete,
        // O fornecedor entra na cesta por pouco e cobra muito para entregar: vale considerar
        // mover o item para outro. Limiar literal, dito na tela.
        freteDesproporcional: frete != null && frete > dados.subtotal,
      };
    });
  }, [comparacao, escolhas]);

  /** Totais da cesta COM frete (at-11) — o custo real da compra que está sendo fechada. */
  const totaisComFrete = useMemo(() => {
    const fretes = cestaPorFornecedor.reduce((s, f) => s + (f.frete ?? 0), 0);
    return {
      fretes,
      total: totaisCesta.escolhido + fretes,
      // Vencedores que não declararam frete: enquanto houver um, o total não está fechado.
      semFrete: cestaPorFornecedor.filter((f) => f.frete == null).length,
      desproporcionais: cestaPorFornecedor.filter((f) => f.freteDesproporcional),
    };
  }, [cestaPorFornecedor, totaisCesta.escolhido]);

  const itensSemCompra = useMemo(
    () => (comparacao?.itens ?? []).filter((i) => !escolhas[i.cotacaoItemId]),
    [comparacao, escolhas],
  );

  if (carregando) {
    return (
      <div
        className="Frame1000004012"
        style={{
          height: '100vh',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'transparent',
        }}
      >
        <div className="pc-carregando">Carregando cotação...</div>
      </div>
    );
  }

  if (!cotacao) {
    return (
      <div
        className="Frame1000004012"
        style={{
          height: '100vh',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'transparent',
        }}
      >
        <div className="pc-vazio">Cotação não encontrada.</div>
      </div>
    );
  }

  const podeComparar = ['EM_DECISAO'].includes(cotacao.status);
  /** Os itens para escolher no convite (at-18). */
  const itensEscolhiveis = cotacao.itens.map((i) => ({
    id: i.id,
    descricao: i.descricao,
    detalhe: `${Number(i.quantidade)} ${i.unidade}`,
  }));
  /** Itens sem compra na cotação concluída: o que o "Recotar itens sem compra" leva (at-18). */
  const itensParaRecotar =
    cotacao.status === 'CONCLUIDA' ? cotacao.itens.filter((i) => !i.decisao?.propostaItemVencedorId) : [];
  const semOferta = cotacao.itensSemOferta ?? [];

  /** Cria a cotação nova com os itens sem compra e abre ela; falha avisa e fica aqui (at-60). */
  async function recotar() {
    if (!cotacao) return;
    setProcessando(true);
    try {
      const nova = await recotarItens(
        cotacao.id,
        itensParaRecotar.map((i) => i.id),
      );
      setConfirmacao(null);
      notify.success('Cotação nova criada com os itens sem compra. Escolha os fornecedores e envie.');
      navigate(`/portal-cotacoes/${nova.id}`);
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível criar a cotação nova.');
    } finally {
      setProcessando(false);
    }
  }

  return (
    <div
      className="Frame1000004012"
      style={{
        height: 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        backgroundColor: 'transparent',
      }}
    >
      <div className="ConteDo recepcao-conte-do pc-page">
        <div className="recepcao-header-row" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {cotacao.numero}
              <button
                type="button"
                className="help-circle-btn"
                title="Como Funciona"
                onClick={() => setAjuda(aba)}
              >
                ?
              </button>
            </div>
            <p className="pc-header-sub" style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7a99' }}>
              {cotacao.veiculo ?? 'Veículo não informado'} {cotacao.placa ? `· ${cotacao.placa}` : ''}
            </p>
          </div>
          <div className="pc-header-acoes" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Atualizar (at-17): a tela já acompanha pelo socket; o botão é para quem quer conferir já. */}
            <span className="pc-texto-secundario" style={{ fontSize: 12 }}>
              {atualizadoEm ? `Atualizado às ${atualizadoEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}
            </span>
            <button type="button" className="pc-btn pc-btn--secundario" disabled={carregando} onClick={() => carregar()}>
              {carregando ? 'Atualizando...' : 'Atualizar'}
            </button>
            <div
              className="CtaBig MouseHover"
              onClick={() => navigate('/portal-cotacoes')}
              style={{
                padding: '7px 16px',
                backgroundColor: 'var(--tema-superficie, white)',
                border: '1px solid var(--tema-linha-2, #CACACA)',
                cursor: 'pointer',
                marginRight: 0,
              }}
            >
              <div className="Frame1000003987" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src={ArrowLeftOrange} alt="" width="16" height="12" />
                <span style={{ color: 'var(--tema-texto, #1A1C1B)', fontWeight: 600, fontSize: '14px', fontFamily: 'AmpleSoft-Bold, sans-serif' }}>
                  Voltar à lista
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Cabeçalho de contexto */}
        <div className="pc-card pc-card--summary">
          <div className="pc-card-corpo">
            <div className="pc-contexto">
              <CotacaoBadge status={cotacao.status} />
              <span className="pc-texto-secundario">
                Solicitação: <strong>{cotacao.solicitacaoNumero}</strong>
              </span>
              <span className="pc-texto-secundario">
                Preset: <strong>{cotacao.preset?.nome ?? '—'}</strong>
              </span>
              <span className="pc-texto-secundario">
                {/* Só a data desde a at-17: o prazo vale o dia inteiro. */}
                Respostas até: <strong>{dataDoPrazo(cotacao.prazoRespostaAte)}</strong>
              </span>
              {prazoVencido(cotacao.status, cotacao.prazoRespostaAte) && <PrazoVencidoBadge />}
              <span className="pc-texto-secundario">
                Comprador: <strong>{cotacao.compradorNome ?? '—'}</strong>
              </span>
              {/* Chassi e local de entrega da requisição (at-17, Alessandra): só para o comprador. */}
              <span className="pc-texto-secundario">
                Chassi: <strong>{cotacao.chassi ?? 'Não informado'}</strong>
              </span>
              <span
                className="pc-texto-secundario"
                title={[cotacao.localEntregaNome, cotacao.localEntregaEndereco].filter(Boolean).join(' - ') || undefined}
              >
                Local de entrega:{' '}
                <strong>
                  {[cotacao.localEntregaNome, cotacao.localEntregaEndereco].filter(Boolean).join(' - ') || 'Não informado'}
                </strong>
              </span>
            </div>

            {/*
              Motivo da Requisição (at-17, pedido do Victor em 30/10/2026): o texto que o comprador lê no
              ERP, copiado na criação. Bloco próprio porque é texto de várias linhas; só para o comprador.
            */}
            <div className="pc-texto-secundario" style={{ marginTop: 10, marginBottom: 16, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              Motivo da Requisição:{' '}
              {cotacao.motivoRequisicao ? <strong>{cotacao.motivoRequisicao}</strong> : <strong>Não informado</strong>}
            </div>

            <ItensSemOfertaFaixa
              status={cotacao.status}
              itens={cotacao.itensSemOferta}
              podeAgir={podeAnexar}
              onConvidar={convidar}
              onReabrir={() => setConfirmacao('reabrir')}
            />

            <div className="pc-acoes-estado">
              {cotacao.status === 'RASCUNHO' && (
                <button type="button" className="pc-btn pc-btn--primario" onClick={() => setConfirmacao('enviar')}>
                  Enviar convites
                </button>
              )}
              {['ENVIADA', 'EM_RESPOSTA'].includes(cotacao.status) && (
                <button type="button" className="pc-btn pc-btn--primario" onClick={() => setConfirmacao('encerrar')}>
                  Encerrar respostas e iniciar negociação
                </button>
              )}
              {/* Convidar outro fornecedor depois do envio (at-17). TI só lê (ADR 0012). */}
              {podeAnexar && ['ENVIADA', 'EM_RESPOSTA'].includes(cotacao.status) && (
                <button type="button" className="pc-btn pc-btn--secundario" onClick={() => convidar()}>
                  Convidar fornecedor
                </button>
              )}
              {/* Reabrir para respostas (at-18): a API existia desde o MVP e nenhuma tela a chamava. */}
              {podeAnexar && cotacao.status === 'EM_DECISAO' && (
                <button type="button" className="pc-btn pc-btn--secundario" onClick={() => setConfirmacao('reabrir')}>
                  Reabrir para respostas
                </button>
              )}
              {/* A ajuda prometia este botão e ele não existia (at-18). */}
              {podeAnexar && itensParaRecotar.length > 0 && (
                <button type="button" className="pc-btn pc-btn--secundario" onClick={() => setConfirmacao('recotar')}>
                  Recotar itens sem compra
                </button>
              )}
              {podeComparar && (
                <button type="button" className="pc-btn pc-btn--primario" onClick={() => setAba('comparacao')}>
                  Abrir comparação
                </button>
              )}
              {cotacao.status === 'GERANDO_PEDIDOS' && (
                <button
                  type="button"
                  className="pc-btn pc-btn--primario"
                  onClick={() => setConfirmacao('gerar-pedidos')}
                >
                  Gerar pedidos no ERP
                </button>
              )}
              {['RASCUNHO', 'ENVIADA', 'EM_RESPOSTA', 'EM_DECISAO'].includes(cotacao.status) && (
                <button type="button" className="pc-btn pc-btn--secundario" onClick={() => setConfirmacao('cancelar')}>
                  Cancelar cotação
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Abas quando há comparação */}
        {comparacao && (
          <div className="recepcao-tabs">
            <button
              type="button"
              className={`recepcao-tab ${aba === 'painel' ? 'active' : ''}`}
              onClick={() => setAba('painel')}
            >
              Painel
            </button>
            <button
              type="button"
              className={`recepcao-tab ${aba === 'comparacao' ? 'active' : ''}`}
              onClick={() => setAba('comparacao')}
            >
              Comparação
            </button>
            <button
              type="button"
              className={`recepcao-tab ${aba === 'cesta' ? 'active' : ''}`}
              onClick={() => setAba('cesta')}
            >
              Revisão da cesta
            </button>
          </div>
        )}

      {/* ── PAINEL ─────────────────────────────────────────────────────────── */}
      {aba === 'painel' && (
        <>
          <div className="pc-card">
            <h2 className="pc-card-titulo">
              Fornecedores — {responderam} de {convidados} responderam
            </h2>
            {cotacao.convites.length === 0 ? (
              <div className="Tabela">
                <div className="Item" style={{ paddingLeft: 30, paddingRight: 30 }}>
                  <div className="Data">Nenhum fornecedor convidado.</div>
                </div>
              </div>
            ) : (
              <div className="TabelaScroll" style={{ width: '100%', boxSizing: 'border-box' }}>
                <div
                  className="TabelaContent"
                  style={{
                    /*
                      ⚠️ NUNCA usar `auto`/`max-content` neste padrão de tabela (tentado e
                      revertido em 12/08/2026): o cabeçalho e CADA LINHA são grids
                      independentes (`.TabelaContent > .Tabela` é um grid por linha), então
                      trilha dimensionada por conteúdo é medida por linha — cada linha sai
                      com colunas em posições diferentes e nada alinha com o cabeçalho.
                      A regra que funciona: PX FIXO no que é curto por natureza (badge,
                      canal, botões — o valor resolve igual em todos os grids) e `fr` só
                      nas colunas de texto, que dividem a sobra.
                    */
                    /*
                     * Redimensionada na at-07, e as duas mudanças têm a MESMA causa: a última
                     * coluna tinha 330px porque abrigava cinco botões, e a de canal tinha
                     * 130px porque só dizia "email, whatsapp".
                     *
                     * Hoje a última tem um botão ("Ações", ~85px) — os 330px deixavam um
                     * vazio de ~250px ao lado da tabela — e a coluna "Envio" carrega texto de
                     * verdade ("E-mail enviado · WhatsApp falhou"), que em 130px truncava
                     * antes de dizer qualquer coisa. Uma virou `120px`, a outra ganhou `fr`.
                     */
                    ['--table-columns' as any]: '2.4fr 110px 1.7fr 1.3fr 120px',
                    width: '100%',
                    minWidth: '860px',
                  }}
                >
                  <div className="Tabela TabelaHeader">
                    <div className="Placa">
                      <div className="TTulo">
                        <div className="Opcoes">Fornecedor</div>
                        <div className="Opcoes">Status</div>
                        {/* "Envio", não "Canal": a coluna passou a dizer o desfecho (at-07). */}
                        <div className="Opcoes">Envio</div>
                        <div className="Opcoes">Acesso/Resposta</div>
                        <div className="Opcoes">Ações</div>
                      </div>
                    </div>
                  </div>
                  {cotacao.convites.map((convite) => (
                    <div key={convite.id} className="Tabela" style={{ paddingLeft: 30, paddingRight: 30 }}>
                      <div className="Item">
                        {/*
                          UMA linha por regra do Victor (12/08/2026): célula de tabela nunca
                          empilha duas linhas — nome e CNPJ ficam lado a lado; nome longo
                          corta com reticências (o `title` mostra inteiro no hover).
                        */}
                        <div className="Data" style={{ gap: 8, minWidth: 0 }}>
                          {/*
                            Respondeu → o nome vira link e abre a PROPOSTA (12/08/2026):
                            "Respondeu em ..." sem os valores obrigava a esperar o
                            encerramento para saber o que veio. Padrão da casa: o nome é o
                            link, como a placa na Recepção.
                          */}
                          {convite.propostas.length > 0 ? (
                            <span
                              onClick={() => setPropostaAbertaId(convite.id)}
                              title={`${convite.fornecedor.razaoSocial} — ver a proposta enviada`}
                              style={{
                                fontWeight: 600,
                                fontSize: '12px',
                                color: '#ea5d0e',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {convite.fornecedor.razaoSocial}
                            </span>
                          ) : (
                            <span
                              title={convite.fornecedor.razaoSocial}
                              style={{
                                fontWeight: 600,
                                color: 'var(--tema-texto, #1A1C1B)',
                                fontSize: '12px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {convite.fornecedor.razaoSocial}
                            </span>
                          )}
                          <span style={{ fontSize: '11px', color: '#6b7a99', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {convite.fornecedorCnpj}
                          </span>
                        </div>
                      </div>
                      <div className="Item">
                        <ConviteBadge status={convite.status} />
                      </div>
                      {/*
                        ENVIO REAL, não os canais configurados (at-07).
                        Antes esta célula listava `canais` ("email, whatsapp"), que é
                        configuração, não desfecho. Com o status do convite dizendo "Enviado"
                        desde antes de os disparos rodarem, a linha inteira afirmava sucesso
                        para um e-mail que nunca saiu. Agora diz o que aconteceu com cada canal.
                      */}
                      <div className="Item">
                        <EnvioPorCanal convite={convite} />
                      </div>
                      <div className="Item">
                        <span className="Data">
                          {convite.respondidoEm ? (
                            `Respondeu em ${dataHora(convite.respondidoEm)}`
                          ) : convite.primeiroAcessoEm ? (
                            `Abriu em ${dataHora(convite.primeiroAcessoEm)}`
                          ) : (
                            '—'
                          )}
                        </span>
                      </div>
                      {/*
                        UM elemento na célula (at-07, regra do Victor de 12/08/2026: célula de
                        tabela não quebra em duas linhas). Eram cinco botões com `flex-wrap`,
                        que quebravam de fato — e nenhum deles tinha espaço para dizer o que
                        fazia, o que importa aqui: "Novo link" INVALIDA o link do fornecedor e
                        "Reenviar" não, distinção invisível numa fileira de botões iguais.

                        RASCUNHO NÃO TEM AÇÕES (12/08/2026): todas entregam link ao fornecedor,
                        e em rascunho a cotação nem foi enviada — o link abria o formulário de
                        algo que ainda pode mudar, e a proposta era recusada no fim. O caminho
                        em rascunho é "Enviar convites"; o backend recusa as ações também.
                      */}
                      {/* Alinhado à ESQUERDA, como o cabeçalho e como todas as colunas do
                          sistema (determinação do Vinicius). */}
                      <div className="Item">
                        {['ENVIADA', 'EM_RESPOSTA'].includes(cotacao.status) && (
                          <button
                            type="button"
                            className="pc-btn-acoes"
                            disabled={processando}
                            aria-label={`Ações — ${convite.fornecedor.razaoSocial}`}
                            onClick={() => setConviteAberto(convite.id)}
                          >
                            Ações
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="pc-card">
            <h2 className="pc-card-titulo">Itens da cotação</h2>
            <div className="TabelaScroll" style={{ width: '100%', boxSizing: 'border-box' }}>
              <div
                className="TabelaContent"
                style={{
                  /*
                    O PADRÃO DAS TABELAS DO SISTEMA, sem invenção (12/08/2026, depois de 3
                    variações reprovadas): colunas proporcionais e TUDO alinhado à esquerda
                    — Recepção, Tela de Controle e a config de Cotações são assim, e
                    "colunas alinhadas à esquerda" é determinação registrada do Vinicius.
                    Código em px (8 dígitos, largura conhecida); Descrição com a maior
                    fatia por ser o único texto; Qtd e Un. dividem o resto com o valor
                    embaixo do rótulo. Reprovados: Qtd/Un na borda direita (ilha), coluna
                    espaçadora (cabeçalho laranja com cauda vazia) e `auto`/`max-content`
                    (cada linha é um grid próprio — mede a coluna pelo conteúdo DELA e
                    desalinha tudo; ver a tabela de fornecedores acima).
                  */
                  ['--table-columns' as any]: '140px 2.2fr 1fr 1fr',
                  width: '100%',
                  minWidth: '700px',
                }}
              >
                <div className="Tabela TabelaHeader">
                  <div className="Placa">
                    <div className="TTulo">
                      <div className="Opcoes">Código</div>
                      <div className="Opcoes">Descrição</div>
                      <div className="Opcoes">Qtd</div>
                      <div className="Opcoes">Un.</div>
                    </div>
                  </div>
                </div>
                {cotacao.itens.map((item) => (
                  <div key={item.id} className="Tabela" style={{ paddingLeft: 30, paddingRight: 30 }}>
                    <div className="Item">
                      <span className="Data pc-mono">{item.codigoProduto}</span>
                    </div>
                    <div className="Item">
                      <span className="Data">{item.descricao}</span>
                    </div>
                    <div className="Item">
                      <span className="Data">{Number(item.quantidade)}</span>
                    </div>
                    <div className="Item">
                      <span className="Data">{item.unidade}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {pedidos.length > 0 && (
            <div className="pc-card">
              <h2 className="pc-card-titulo">Pedidos gerados</h2>
              <div className="TabelaScroll" style={{ width: '100%', boxSizing: 'border-box' }}>
                <div
                  className="TabelaContent"
                  style={{
                    ['--table-columns' as any]: '3fr 1.5fr 2fr 1.5fr 1fr',
                    width: '100%',
                    minWidth: '850px',
                  }}
                >
                  <div className="Tabela TabelaHeader">
                    <div className="Placa">
                      <div className="TTulo">
                        <div className="Opcoes">Fornecedor</div>
                        <div className="Opcoes">Referência</div>
                        <div className="Opcoes">Status</div>
                        <div className="Opcoes">Previsão</div>
                        <div className="Opcoes" style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end', paddingRight: 30 }}>Ações</div>
                      </div>
                    </div>
                  </div>
                  {pedidos.map((p) => (
                    <div key={p.id} className="Tabela" style={{ paddingLeft: 30, paddingRight: 30 }}>
                      <div className="Item">
                        <span className="Data" style={{ fontWeight: 600, color: 'var(--tema-texto, #1A1C1B)' }}>{p.fornecedor?.razaoSocial ?? p.fornecedorCnpj}</span>
                      </div>
                      <div className="Item">
                        <span className="Data pc-mono">{p.numeroExterno}</span>
                      </div>
                      <div className="Item">
                        {p.status === 'ENVIADO' ? (
                          <span className="Data" style={{ color: 'var(--tema-texto-verde, #065f46)', fontWeight: 500 }}>
                            Enviado{p.simulado ? ' (simulação)' : ''}
                          </span>
                        ) : p.status === 'ERRO' ? (
                          <span className="Data" style={{ color: 'var(--tema-texto-vermelho, #b42318)', fontWeight: 500 }}>
                            Erro: {p.ultimoErro}
                          </span>
                        ) : (
                          <span className="Data">Pendente</span>
                        )}
                      </div>
                      <div className="Item">
                        <span className="Data">
                          {p.previsaoDeEntrega ? new Date(p.previsaoDeEntrega).toLocaleDateString('pt-BR') : '—'}
                        </span>
                      </div>
                      <div className="Item" style={{ justifyContent: 'flex-end', paddingRight: 16 }}>
                        {p.status === 'ERRO' && (
                          <button
                            type="button"
                            className="pc-btn pc-btn--secundario pc-btn--sm"
                            onClick={() => executar(() => reenviarPedido(p.id), 'Pedido reenviado.')}
                          >
                            Tentar de novo
                          </button>
                        )}
                        {/*
                          Autorização de faturamento (at-11): SÓ em pedido ENVIADO. Autorizar o
                          faturamento de um pedido que falhou no ERP faria o fornecedor emitir
                          nota contra um pedido que não existe lá — e a nota chegaria sem par.
                          O backend recusa igualmente (`409 PEDIDO_NAO_ENVIADO`).
                        */}
                        {p.status === 'ENVIADO' && (
                          <button
                            type="button"
                            className="pc-btn pc-btn--secundario pc-btn--sm"
                            disabled={processando}
                            onClick={() =>
                              setConfirmacaoEmail({
                                finalidade: 'AUTORIZACAO_FATURAMENTO',
                                fornecedorCnpj: p.fornecedorCnpj,
                                fornecedorNome: p.fornecedor?.razaoSocial ?? p.fornecedorCnpj,
                                pedidoId: p.id,
                              })
                            }
                          >
                            Autorizar faturamento por e-mail
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/*
                O último envio de cada autorização vive ABAIXO da tabela, não dentro da célula
                de ações: célula é uma linha só (regra de 12/08/2026), e a frase tem data,
                autor, endereço e — quando falha — o motivo.
              */}
              {podeEmailFornecedor && pedidos.some((p) => p.status === 'ENVIADO') && (
                <div className="pc-card-corpo">
                  <p className="pc-hint" style={{ marginTop: 0, marginBottom: 6 }}>
                    Autorização de faturamento por e-mail
                  </p>
                  {pedidos
                    .filter((p) => p.status === 'ENVIADO')
                    .map((p) => (
                      <p key={p.id} style={{ margin: '0 0 4px', fontSize: 13 }}>
                        <strong>{p.fornecedor?.razaoSocial ?? p.fornecedorCnpj}</strong>:{' '}
                        {linhaUltimoEnvio(ultimoEmail(p.fornecedorCnpj, 'AUTORIZACAO_FATURAMENTO'))}
                      </p>
                    ))}
                </div>
              )}
            </div>
          )}

          {/*
            Imagens internas (at-11). No painel e em TODAS as etapas: a foto da peça ajuda
            desde o rascunho, e continua valendo depois do pedido (costuma chegar com a peça).
            O TI lê e não opera — o gating é `podeAnexar`, com os mesmos papéis do `@Roles`.
          */}
          <AnexosSection cotacaoId={cotacao.id} itens={cotacao.itens} podeEditar={podeAnexar} />

          {/*
            Foto do veículo (at-16): vai na prévia do link e na tela do fornecedor. Troca só
            enquanto a cotação recebe respostas, com os papéis do `@Roles` da rota.
          */}
          <FotoVeiculoSection
            cotacaoId={cotacao.id}
            onEstado={setEstadoDaFoto}
            pedidoDeEscolha={pedidoDeEscolhaDaFoto}
            podeEditar={podeAnexar && ['RASCUNHO', 'ENVIADA', 'EM_RESPOSTA'].includes(cotacao.status)}
          />

          {/* Fotos que o fornecedor vê (at-17): dano e oficina externa. Mesmo gating da foto do veículo. */}
          <FotosFornecedorSection
            cotacaoId={cotacao.id}
            itens={cotacao.itens}
            podeEditar={podeAnexar && ['RASCUNHO', 'ENVIADA', 'EM_RESPOSTA'].includes(cotacao.status)}
          />
        </>
      )}

      {/* ── COMPARAÇÃO ─────────────────────────────────────────────────────── */}
      {aba === 'comparacao' && comparacao && (
        <>
          <div className="pc-legenda">
            <span className="pc-marca pc-marca--preco">P</span> menor preço
            <span className="pc-marca pc-marca--score">S</span> melhor score
            {/*
              `?` localizado (13/08/2026): a pergunta "de onde saiu esse 0,918?" nasce olhando
              a célula, e quem a faz não vai procurar o drawer da aba. Fica colado na legenda
              do score, que é o que a origina.
            */}
            <button
              type="button"
              className="help-circle-btn"
              title="Como o score é calculado"
              onClick={() => setAjudaScoreAberta(true)}
            >
              ?
            </button>
            <span className="pc-texto-secundario" style={{ marginLeft: 'auto' }}>
              Pesos: preço {comparacao.pesos.preco}% · prazo {comparacao.pesos.prazo}%
              {comparacao.pesos.qualidade > 0 ? ` · qualidade ${comparacao.pesos.qualidade}%` : ''}
            </span>
            <span className="pc-rolagem-setas" title="A tabela também rola arrastando com o mouse">
              <button type="button" className="pc-btn pc-btn--secundario pc-btn--sm" onClick={() => matriz.rolar(-1)} aria-label="Rolar a tabela para a esquerda">
                &larr;
              </button>
              <button type="button" className="pc-btn pc-btn--secundario pc-btn--sm" onClick={() => matriz.rolar(1)} aria-label="Rolar a tabela para a direita">
                &rarr;
              </button>
            </span>
          </div>

          <ComoFunciona
            isOpen={ajudaScoreAberta}
            onClose={() => setAjudaScoreAberta(false)}
            titulo="Como Funciona - Cálculo do score"
            secoes={helpScore}
          />

          {/* `--tabela`: a matriz vai de borda a borda do cartão (sem a faixa branca que o
              padding do card abria acima da barra do cabeçalho). */}
          <div className="pc-card pc-card--tabela">
            <div className="pc-tabela-wrapper pc-tabela-wrapper--arrastavel" ref={matriz.ref}>
              {/* Layout fixo + colgroup: toda coluna de fornecedor recebe a MESMA largura,
                  independente do tamanho do nome (pedido do Victor, 13/10/2026 — nome longo
                  dava "vantagem" visual ao fornecedor). O piso por coluna vira largura mínima
                  da tabela, que então rola em vez de espremer. */}
              <table
                className="pc-tabela pc-matriz pc-matriz--igual"
                style={{ minWidth: 240 + comparacao.porFornecedor.length * 170 }}
              >
                <colgroup>
                  <col className="pc-matriz-col-item" />
                  {comparacao.porFornecedor.map((f) => (
                    <col key={f.fornecedorCnpj} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th className="pc-matriz-item">Item</th>
                    {comparacao.porFornecedor.map((f) => (
                      <th key={f.fornecedorCnpj} style={{ textAlign: 'center' }}>
                        {f.fornecedorNome}
                        {!f.completo && f.respondeu && (
                          <div className="pc-texto-secundario" style={{ fontWeight: 400 }}>
                            cotou {f.itensCotados} de {f.totalItens}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparacao.itens.map((item) => {
                    const escolhaId = escolhas[item.cotacaoItemId];
                    return (
                      <tr key={item.cotacaoItemId}>
                        <td className="pc-matriz-item">
                          <div style={{ fontWeight: 500 }}>{item.descricao}</div>
                          <div className="pc-texto-secundario pc-mono">
                            {item.codigo} · {item.quantidade} {item.unidade}
                          </div>
                          {item.propostaUnica && <div className="pc-texto-secundario">proposta única</div>}

                          {/*
                            Estado do rascunho DESTA peça (13/08/2026). Fica na coluna do
                            item — que é a congelada — para continuar visível por mais que a
                            matriz role para a direita.
                          */}
                          {/*
                            Tags de estado da aprovação (14/08/2026, pedido do Victor):
                            visíveis em QUALQUER status da cotação — a versão anterior só
                            renderizava em EM_DECISAO, então com a cotação AGUARDANDO
                            APROVAÇÃO a matriz não dizia qual item estava pendente e qual
                            já tinha sido resolvido. Mesmas classes de badge da lista e da
                            tela de Aprovações (o CSS chega pelo import do CotacaoBadge).
                          */}
                          {item.aprovacao?.status === 'APROVADA' && (
                            <div className="pc-rascunho-linha">
                              <span
                                className="pc-badge pc-badge--verde"
                                title="Aprovado pelo gestor — este item não volta a ser decidido"
                              >
                                <span className="pc-badge-dot" />
                                Aprovado
                              </span>
                            </div>
                          )}
                          {item.aprovacao?.status === 'PENDENTE' && (
                            <div className="pc-rascunho-linha">
                              <span
                                className="pc-badge pc-badge--laranja"
                                title="Escolha acima do menor preço aguardando o gestor de compras"
                              >
                                <span className="pc-badge-dot" />
                                Aguardando aprovação
                              </span>
                            </div>
                          )}
                          {item.aprovacao?.status === 'REPROVADA' && (
                            <div className="pc-rascunho-linha">
                              <span className="pc-badge pc-badge--vermelho">
                                <span className="pc-badge-dot" />
                                {estadoRascunho.get(item.cotacaoItemId) === 'reaberto'
                                  ? 'Reprovado — escolha novamente'
                                  : 'Reprovado'}
                              </span>
                              {/* O comentário orienta a nova escolha — visível, não só em hover. */}
                              {item.aprovacao.comentario && (
                                <div className="pc-reprovacao-comentario">{item.aprovacao.comentario}</div>
                              )}
                            </div>
                          )}
                          {!item.aprovacao && item.bloqueado && (
                            <div className="pc-rascunho-linha">
                              <span
                                className="pc-badge pc-badge--cinza"
                                title="Fechado no menor preço ou sem compra — não precisou do gestor e não volta a ser decidido"
                              >
                                <span className="pc-badge-dot" />
                                Fechado sem divergência
                              </span>
                            </div>
                          )}
                          {comparacao.status === 'EM_DECISAO' &&
                            (() => {
                              const estado = estadoRascunho.get(item.cotacaoItemId);
                              if (!estado) return null;
                              /*
                                ⚠️ Envolvido num bloco próprio: o selo é `inline-block` e o
                                `Referencias` logo abaixo é `inline-flex` — soltos, os dois
                                fluem INLINE e ficam lado a lado sempre que couberem na
                                largura da coluna. Era isso que fazia o "rascunho salvo"
                                aparecer grudado nas referências numa linha e sozinho em
                                outra, conforme o tamanho do texto de cada peça.
                              */
                              return (
                                <div className="pc-rascunho-linha">
                                  {/*
                                    O estado 'reaberto' não rende selo próprio: a tag
                                    vermelha de aprovação acima (com o comentário do
                                    gestor) já comunica tudo — ele segue existindo para
                                    travar a cesta e o salvar em lote.
                                  */}
                                  {estado === 'salvo' && (
                                    <span className="pc-rascunho pc-rascunho--salvo">rascunho salvo</span>
                                  )}
                                  {estado === 'bloqueado' && (
                                    <span
                                      className="pc-rascunho pc-rascunho--bloqueado"
                                      title="Escolha acima do menor preço: escreva a justificativa para poder salvar"
                                    >
                                      falta justificar
                                    </span>
                                  )}
                                  {estado === 'alterado' && (
                                    <button
                                      type="button"
                                      className="pc-rascunho pc-rascunho--botao"
                                      disabled={processando}
                                      title="Guarda a escolha desta peça sem fechar a cesta"
                                      onClick={() => pedirSalvarRascunho(item.cotacaoItemId)}
                                    >
                                      Salvar rascunho do item
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                          {/*
                            Referências do orçamento na coluna do ITEM (12/08/2026): valem
                            para a linha inteira, então ficam aqui e não repetidas em cada
                            célula de fornecedor. O desvio por proposta não entra aqui — na
                            matriz quem compara é o score; o desvio individual está no modal
                            da proposta recebida.
                          */}
                          {(() => {
                            const doItem = cotacao.itens.find((i) => i.id === item.cotacaoItemId);
                            return doItem ? (
                              <Referencias
                                formato="bloco"
                                valorOrcado={doItem.valorOrcado}
                                mediaCompras={doItem.mediaCompras}
                                mediaComprasQtd={doItem.mediaComprasQtd}
                                precoOnline={doItem.precoOnline}
                                precoOnlineUrl={doItem.precoOnlineUrl}
                                precoOnlinePesquisadoEm={doItem.precoOnlinePesquisadoEm}
                                precoOnlineConferir={doItem.precoOnlineConferir}
                              />
                            ) : null;
                          })()}
                        </td>
                        {comparacao.porFornecedor.map((f) => {
                          /*
                           * Todas as OFERTAS do fornecedor para o item (portal-cotacoes/at-16): ele pode
                           * oferecer a mesma peça em condições diferentes (nova, retirada…), e cada uma
                           * é escolhível. Uma oferta só é o caso de sempre.
                           */
                          const ofertas = item.propostas.filter((x) => x.fornecedorCnpj === f.fornecedorCnpj);
                          if (ofertas.length === 0) {
                            const naoCotou = item.naoCotaram.some((n) => n.fornecedorCnpj === f.fornecedorCnpj);
                            // Quem não recebeu o item nunca foi perguntado sobre ele (at-18).
                            const naoConvidado = (item.naoConvidados ?? []).some((n) => n.fornecedorCnpj === f.fornecedorCnpj);
                            return (
                              <td key={f.fornecedorCnpj} style={{ textAlign: 'center' }}>
                                <span className="pc-texto-secundario">
                                  {naoConvidado ? 'Não recebeu' : naoCotou ? 'Não cotou' : 'Sem resposta'}
                                </span>
                              </td>
                            );
                          }
                          return (
                            <td key={f.fornecedorCnpj} style={{ textAlign: 'center' }}>
                              {ofertas.map((p) => {
                              const selecionada = escolhaId === p.propostaItemId;
                              return (
                              <button
                                key={p.propostaItemId}
                                type="button"
                                className={`pc-celula ${selecionada ? 'pc-celula--escolhida' : ''}`}
                                onClick={() => selecionarProposta(item, p)}
                                // Item liquidado não aceita mais escolha (reabertura é só
                                // do item reprovado, 13/08/2026) — o backend recusa; aqui
                                // o botão nem oferece.
                                disabled={comparacao.status !== 'EM_DECISAO' || item.bloqueado}
                              >
                                <span className="pc-celula-marcas">
                                  {p.flags.menorPreco && <span className="pc-marca pc-marca--preco">P</span>}
                                  {p.flags.melhorScore && <span className="pc-marca pc-marca--score">S</span>}
                                </span>
                                <span className="pc-celula-preco">{moeda(p.totalItem)}</span>
                                <span className="pc-texto-secundario">{prazoTexto(p.prazoEntregaDias)}</span>
                                {p.condicao && <span className="pc-texto-secundario">{p.condicao}</span>}
                                {p.marcaFabricante && (
                                  <span className="pc-texto-secundario">{p.marcaFabricante}</span>
                                )}
                                {(p.fotos ?? 0) > 0 && (
                                  <span className="pc-texto-secundario">
                                    {p.fotos === 1 ? '1 foto' : `${p.fotos} fotos`} (veja na proposta)
                                  </span>
                                )}
                                {p.score !== null && (
                                  <span className="pc-celula-score">score {p.score.toFixed(3)}</span>
                                )}
                                {selecionada && justificativaEfetiva(item) && (
                                  <span className="pc-celula-just">{justificativaEfetiva(item)}</span>
                                )}
                              </button>
                              );
                              })}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="pc-card">
            <div className="pc-card-corpo pc-rodape-cesta">
              <div className="pc-rodape-resumo">
                <div className="pc-rodape-kpis">
                  <div className="pc-rodape-kpi pc-rodape-kpi--total">
                    <span className="pc-resumo-rotulo">Total da cesta</span>
                    <span className="pc-rodape-kpi-valor">{moeda(totaisCesta.escolhido)}</span>
                  </div>
                  <div className="pc-rodape-kpi">
                    <span className="pc-resumo-rotulo">Tudo no menor preço</span>
                    <span className="pc-rodape-kpi-valor">{moeda(totaisCesta.menorPreco)}</span>
                  </div>
                  <div className="pc-rodape-kpi">
                    <span className="pc-resumo-rotulo">Diferença</span>
                    <span
                      className={`pc-rodape-kpi-valor${totaisCesta.escolhido > totaisCesta.menorPreco ? ' pc-rodape-kpi-valor--laranja' : ''}`}
                    >
                      {moeda(totaisCesta.escolhido - totaisCesta.menorPreco)}
                    </span>
                    <span className="pc-rodape-kpi-apoio">
                      {totaisCesta.divergentes > 0
                        ? `${totaisCesta.divergentes} ${totaisCesta.divergentes > 1 ? 'itens' : 'item'} acima do menor preço`
                        : 'tudo no menor preço'}
                    </span>
                  </div>
                </div>
                {(totaisCesta.divergentes > 0 || totaisCesta.semJustificativa.length > 0 || itensAlterados.length > 0) && (
                  <div className="pc-rodape-avisos">
                    {totaisCesta.divergentes > 0 && (
                      <p className="pc-aviso pc-aviso--atencao">
                        {totaisCesta.divergentes} {totaisCesta.divergentes > 1 ? 'itens' : 'item'} acima do menor
                        preço — {totaisCesta.divergentes > 1 ? 'exigirão' : 'exigirá'} aprovação do gestor.
                      </p>
                    )}
                    {/*
                      Falta justificar (13/08/2026): diz QUAL item e QUAL é o gesto. Sem isto o
                      comprador clicava em salvar e recebia o 400 do backend, sem saber o que
                      fazer para destravar.
                    */}
                    {totaisCesta.semJustificativa.length > 0 && (
                      <p className="pc-aviso pc-aviso--critico">
                        Falta justificar {totaisCesta.semJustificativa.length === 1 ? 'o item' : 'os itens'}{' '}
                        <strong>{totaisCesta.semJustificativa.join(', ')}</strong> — clique na proposta escolhida para
                        escrever o motivo.
                      </p>
                    )}
                    {/*
                      Quanto do trabalho ainda não está guardado (13/08/2026). Sem isto o
                      comprador não tem como saber o que perderia ao sair da tela.
                    */}
                    {itensAlterados.length > 0 && (
                      <p className="pc-aviso pc-aviso--info">
                        {itensAlterados.length}{' '}
                        {itensAlterados.length === 1 ? 'item com alteração não salva' : 'itens com alterações não salvas'}
                        {' '}— salve o rascunho para poder revisar a cesta.
                      </p>
                    )}
                  </div>
                )}
              </div>
              {/*
                Os dois botões num grupo (13/08/2026): o rodapé é `justify-content:
                space-between`, então três filhos soltos se espalhavam pela largura toda e os
                botões ficavam distantes um do outro. Agrupados, o texto vai para a esquerda e
                a dupla de ações fica junta à direita.
              */}
              <div className="pc-rodape-acoes">
              <button
                type="button"
                className="pc-btn pc-btn--secundario"
                onClick={() => pedirSalvarRascunho()}
                /*
                  Só guarda o que já está pronto. Fica habilitado mesmo havendo item sem
                  justificativa — é justamente aí que salvar o resto tem valor; o que não
                  puder ir fica de fora e o toast diz quantos foram.
                */
                disabled={processando || comparacao.status !== 'EM_DECISAO' || itensAlterados.length === 0}
                title={
                  itensAlterados.length === 0
                    ? 'Nada de novo para guardar'
                    : 'Guarda as escolhas já feitas sem fechar a cesta'
                }
              >
                Salvar rascunho
              </button>
              <button
                type="button"
                className="pc-btn pc-btn--primario"
                onClick={() => setAba('cesta')}
                disabled={processando || comparacao.status !== 'EM_DECISAO' || !cestaPronta}
                title={
                  cestaPronta
                    ? 'Ver a cesta montada, fornecedor a fornecedor'
                    : itensReabertos.length > 0
                      ? 'Há item reprovado pelo gestor aguardando nova escolha'
                      : totaisCesta.semJustificativa.length > 0
                        ? 'Justifique os itens acima do menor preço e salve o rascunho antes de revisar'
                        : 'Salve o rascunho antes de revisar — a cesta é montada a partir do que está gravado'
                }
              >
                Revisar cesta
              </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── REVISÃO DA CESTA ───────────────────────────────────────────────── */}
      {aba === 'cesta' && comparacao && (
        <>
          {/*
            O aviso "os totais não incluem frete" (02/08/2026) SAIU na at-11: ele passaria a
            mentir, porque o frete agora é coletado do fornecedor e entra no total da cesta.
            No lugar dele, dois avisos que descrevem o que o número tem — e o que falta nele.
          */}
          {totaisComFrete.semFrete > 0 && (
            <div className="pc-aviso pc-aviso--atencao">
              {totaisComFrete.semFrete === 1 ? 'Um fornecedor' : `${totaisComFrete.semFrete} fornecedores`} desta
              cesta {totaisComFrete.semFrete === 1 ? 'não informou' : 'não informaram'} o frete — a proposta é
              anterior a esse campo. O total abaixo <strong>não inclui</strong> esse frete; confirme com{' '}
              {totaisComFrete.semFrete === 1 ? 'ele' : 'eles'} antes de fechar.
            </div>
          )}
          {totaisComFrete.desproporcionais.length > 0 && (
            <div className="pc-aviso pc-aviso--atencao">
              Frete alto para o que {totaisComFrete.desproporcionais.length === 1 ? 'o fornecedor leva' : 'levam'}:{' '}
              <strong>
                {totaisComFrete.desproporcionais
                  .map((f) => `${f.nome} (${moeda(f.frete ?? 0)} de frete para ${moeda(f.subtotal)} em peças)`)
                  .join('; ')}
              </strong>
              . Pode compensar mover {totaisComFrete.desproporcionais.length === 1 ? 'esse item' : 'esses itens'}{' '}
              para outro fornecedor da cesta.
            </div>
          )}

          {totaisCesta.divergentes > 0 && (
            <div className="pc-aviso pc-aviso--info">
              {totaisCesta.divergentes} {totaisCesta.divergentes > 1 ? 'itens' : 'item'} acima do menor preço{' '}
              {totaisCesta.divergentes > 1 ? 'vão' : 'vai'} passar pela aprovação do gestor. Nenhum pedido é
              gerado antes do veredito.
            </div>
          )}

          {cestaPorFornecedor.length === 0 ? (
            <div className="pc-card pc-card--summary">
              <div className="pc-vazio">Nenhum item com vencedor definido.</div>
            </div>
          ) : (
            cestaPorFornecedor.map((f) => (
              <div key={f.cnpj} className="pc-card">
                {/*
                  `paddingRight` e não `marginRight` (13/08/2026): `.pc-card-header` é
                  `width: 100%` com `box-sizing: border-box`, então margem à direita empurra
                  o elemento PARA FORA do cartão — o texto ficava colado na borda. Padding
                  entra na conta da largura e afasta o conteúdo, que é o que se queria.
                */}
                <div className="pc-card-header" style={{ paddingRight: 30 }}>
                  <h2 className="pc-card-titulo">{f.nome}</h2>
                  <span className="pc-texto-secundario" style={{ marginBottom: 20 }}>
                    {prazoEntregaFrase(f.maiorPrazo)}
                  </span>
                </div>
                {/*
                  Espelho da nota fiscal (at-11): só depois de a cesta fechar e ser aprovada —
                  antes disso a lista de vencedores ainda muda, e conferir com o fornecedor
                  algo que pode não acontecer é pior que não conferir. O backend recusa
                  igualmente; aqui o botão nem aparece.
                */}
                {podeEmailFornecedor && (
                  <div className="pc-card-corpo" style={{ paddingTop: 0, paddingBottom: 12 }}>
                    <button
                      type="button"
                      className="pc-btn pc-btn--secundario pc-btn--sm"
                      disabled={processando}
                      onClick={() =>
                        setConfirmacaoEmail({
                          finalidade: 'ESPELHO_NF',
                          fornecedorCnpj: f.cnpj,
                          fornecedorNome: f.nome,
                        })
                      }
                    >
                      Solicitar espelho da NF por e-mail
                    </button>
                    <div style={{ marginTop: 6 }}>{linhaUltimoEnvio(ultimoEmail(f.cnpj, 'ESPELHO_NF'))}</div>
                  </div>
                )}
                <div className="TabelaScroll" style={{ width: '100%', boxSizing: 'border-box' }}>
                  <div
                    className="TabelaContent"
                    style={{
                      ['--table-columns' as any]: '6fr 2fr',
                      width: '100%',
                      minWidth: '500px',
                    }}
                  >
                    {f.itens.map((i) => (
                      <div key={i.descricao} className="Tabela" style={{ paddingLeft: 30, paddingRight: 30 }}>
                        <div className="Item">
                          <span className="Data">
                            {i.descricao}
                            {i.divergente && <span className="pc-tag-divergente">acima do menor preço</span>}
                          </span>
                        </div>
                        <div className="Item" style={{ justifyContent: 'flex-end', paddingRight: 16 }}>
                          <span className="Data pc-mono">{moeda(i.total)}</span>
                        </div>
                      </div>
                    ))}
                    {/*
                      O frete do fornecedor (at-11), em linha própria antes do subtotal: ele
                      é da proposta inteira e não de um item, então somá-lo dentro de alguma
                      linha de peça seria mentira sobre o preço daquela peça.
                    */}
                    <div className="Tabela" style={{ paddingLeft: 30, paddingRight: 30 }}>
                      <div className="Item">
                        <span className="Data">
                          Frete
                          {f.freteDesproporcional && (
                            <span className="pc-tag-divergente">maior que as peças deste fornecedor</span>
                          )}
                        </span>
                      </div>
                      <div className="Item" style={{ justifyContent: 'flex-end', paddingRight: 16 }}>
                        <span className="Data pc-mono">
                          {f.frete == null ? 'não informado' : moeda(f.frete)}
                        </span>
                      </div>
                    </div>
                    <div className="Tabela" style={{ paddingLeft: 30, paddingRight: 30 }}>
                      <div className="Item">
                        <span className="Data" style={{ fontWeight: 600 }}>Subtotal</span>
                      </div>
                      <div className="Item" style={{ justifyContent: 'flex-end', paddingRight: 16 }}>
                        <span className="Data pc-mono" style={{ fontWeight: 600 }}>
                          {moeda(f.subtotal + (f.frete ?? 0))}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}

          {itensSemCompra.length > 0 && (
            <div className="pc-card">
              <h2 className="pc-card-titulo">Itens que ficarão sem compra ({itensSemCompra.length})</h2>
              {/* O padding inline daqui saiu em 13/08/2026: era remendo para a
                  `.pc-card-corpo` não ter regra em CSS: agora tem. */}
              <div className="pc-card-corpo">
                {itensSemCompra.map((i) => (
                  <p key={i.cotacaoItemId} style={{ margin: '0 0 4px', fontSize: 13 }}>
                    {i.descricao}{' '}
                    <span className="pc-texto-secundario">
                      {i.propostas.length === 0 ? '(nenhuma proposta recebida)' : '(descartado)'}
                    </span>
                  </p>
                ))}
                <p className="pc-hint" style={{ marginTop: 12 }}>
                  A cotação conclui mesmo assim. Depois de fechar, use Recotar para levar estes itens a uma cotação
                  nova.
                </p>
              </div>
            </div>
          )}

          <div className="pc-card">
            <div className="pc-card-corpo">
              <div className="pc-rodape-cesta">
                <div className="pc-rodape-resumo">
                  {/*
                    O total passou a ser COM frete (at-11), e as peças aparecem em separado:
                    quem confere a compra precisa dos dois números, e um total que misturasse
                    os dois sem dizer esconderia de onde veio a diferença.
                  */}
                  <div className="pc-rodape-kpis">
                    <div className="pc-rodape-kpi pc-rodape-kpi--total">
                      <span className="pc-resumo-rotulo">Total da cesta</span>
                      <span className="pc-rodape-kpi-valor">{moeda(totaisComFrete.total)}</span>
                      <span className="pc-rodape-kpi-apoio">
                        Peças: {moeda(totaisCesta.escolhido)} · Frete: {moeda(totaisComFrete.fretes)}
                        {totaisComFrete.semFrete > 0 && ' (incompleto)'}
                      </span>
                    </div>
                    <div className="pc-rodape-kpi">
                      <span className="pc-resumo-rotulo">Tudo no menor preço</span>
                      <span className="pc-rodape-kpi-valor">{moeda(totaisCesta.menorPreco)}</span>
                      <span className="pc-rodape-kpi-apoio">só peças</span>
                    </div>
                  </div>
                </div>
                <div className="pc-rodape-acoes">
                  <button type="button" className="pc-btn pc-btn--secundario" onClick={() => setAba('comparacao')}>
                    Voltar à comparação
                  </button>
                  <button
                    type="button"
                    className="pc-btn pc-btn--primario"
                    onClick={() => setConfirmacao('fechar-cesta')}
                    disabled={comparacao.status !== 'EM_DECISAO' || cestaPorFornecedor.length === 0}
                  >
                    Fechar cesta
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Modais ─────────────────────────────────────────────────────────── */}

      {/*
        Justificativa da escolha fora do menor preço (reescrito em 28/10/2026, pedido do Victor:
        "esse modal está feio, sem estilo nenhum"). Casco dos modais do Portal
        (`FornecedorDetalhesModal.css`, por import) e o comparativo em dois cartões rotulados — a
        versão anterior empilhava três frases sem hierarquia, e a diferença de preço, que é o que
        o gestor vai cobrar, ficava perdida no meio da terceira.
      */}
      {modalJustificativa && (
        <div className="add-car-modal-overlay" onClick={() => setModalJustificativa(null)}>
          <div className="fornecedor-modal-content pc-justif-modal" onClick={(e) => e.stopPropagation()}>
            <div className="fornecedor-modal-header">
              <div className="fornecedor-modal-header-info">
                <h2>Justifique a escolha</h2>
                <span className="fornecedor-modal-subtitulo" title={modalJustificativa.item.descricao}>
                  {modalJustificativa.item.descricao}
                </span>
              </div>
              <button className="close-button" onClick={() => setModalJustificativa(null)} aria-label="Fechar modal">
                &times;
              </button>
            </div>

            <div className="fornecedor-modal-body pc-justif-corpo">
              <div className="fornecedor-modal-secao">
                <h3>O que está sendo escolhido</h3>
                <div className="pc-justif-comparativo">
                  <div className="fornecedor-info-item pc-justif-cartao pc-justif-cartao--escolhido">
                    <span className="fornecedor-info-label">Escolhido</span>
                    <span className="pc-justif-fornecedor" title={modalJustificativa.proposta.fornecedorNome}>
                      {modalJustificativa.proposta.fornecedorNome}
                    </span>
                    <span className="pc-justif-valor">{moeda(modalJustificativa.proposta.totalItem)}</span>
                    <span className="pc-justif-prazo">{prazoTexto(modalJustificativa.proposta.prazoEntregaDias)}</span>
                  </div>
                  {modalJustificativa.menorPreco && (
                    <div className="fornecedor-info-item pc-justif-cartao">
                      <span className="fornecedor-info-label">Menor preço, preterido</span>
                      <span className="pc-justif-fornecedor" title={modalJustificativa.menorPreco.fornecedorNome}>
                        {modalJustificativa.menorPreco.fornecedorNome}
                      </span>
                      <span className="pc-justif-valor">{moeda(modalJustificativa.menorPreco.totalItem)}</span>
                      <span className="pc-justif-prazo">{prazoTexto(modalJustificativa.menorPreco.prazoEntregaDias)}</span>
                    </div>
                  )}
                </div>
                {modalJustificativa.menorPreco && (
                  <p className="pc-justif-diferenca">
                    Diferença de{' '}
                    <strong>
                      {moeda(modalJustificativa.proposta.totalItem - modalJustificativa.menorPreco.totalItem)}
                    </strong>{' '}
                    a mais que o menor preço. Esta escolha passará pela aprovação do gestor de compras.
                  </p>
                )}
              </div>

              <div className="fornecedor-modal-secao">
                <h3>Motivo</h3>
                <p className="fornecedor-modal-hint">
                  O texto fica gravado na auditoria e é o que o gestor lê para aprovar. Use uma sugestão como
                  ponto de partida ou escreva o seu.
                </p>
                <div className="pc-chips pc-sugestoes">
                  {['Melhor prazo de entrega', 'Peça original / marca', 'Qualidade comprovada', 'Consolidação de entrega'].map(
                    (s) => (
                      <button
                        key={s}
                        type="button"
                        className={`pc-chip${textoJustificativa === s ? ' pc-chip--ativo' : ''}`}
                        onClick={() => setTextoJustificativa(s)}
                      >
                        {s}
                      </button>
                    ),
                  )}
                </div>
                <textarea
                  className="pc-textarea"
                  rows={4}
                  value={textoJustificativa}
                  onChange={(e) => setTextoJustificativa(e.target.value)}
                  placeholder="Explique por que esta é a melhor escolha para a oficina"
                />
              </div>

              <div className="fornecedor-modal-acoes">
                <button type="button" className="fornecedor-modal-btn" onClick={() => setModalJustificativa(null)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="fornecedor-modal-btn fornecedor-modal-btn--primario"
                  disabled={!textoJustificativa.trim()}
                  onClick={confirmarJustificativa}
                >
                  Salvar justificativa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmacaoModal
        isOpen={!!confirmarRascunho}
        titulo="Salvar com a justificativa do preset?"
        descricao={
          <>
            {confirmarRascunho?.itens.length === 1 ? 'Este item foi escolhido' : 'Estes itens foram escolhidos'} pelo
            preset, acima do menor preço, e {confirmarRascunho?.itens.length === 1 ? 'vai' : 'vão'} salvar com a
            justificativa padrão abaixo. Ela fica na auditoria e vai para a aprovação do gestor. Para escrever outro
            motivo, cancele e clique na proposta escolhida.
            <ul className="pc-confirm-itens">
              {confirmarRascunho?.itens.map((i) => (
                <li key={i.id} className="pc-confirm-item">
                  <strong>{i.descricao}</strong>: {i.texto}
                </li>
              ))}
            </ul>
          </>
        }
        rotuloConfirmar="Salvar rascunho"
        carregando={processando}
        onConfirmar={() => {
          const apenas = confirmarRascunho?.apenas;
          setConfirmarRascunho(null);
          void salvarRascunho(apenas);
        }}
        onCancelar={() => setConfirmarRascunho(null)}
      />

      <ConfirmacaoModal
        isOpen={confirmacao === 'enviar'}
        titulo="Enviar os convites?"
        descricao={
          <>
            Serão criados links exclusivos para <strong>{cotacao.convites.length} fornecedor(es)</strong>.{' '}
            {/*
              A frase acompanha o estado da integração (at-07): descrever o envio de WhatsApp
              como manual quando o servidor vai enviar faria o comprador disparar de novo, do
              próprio aparelho, uma mensagem que já saiu.
            */}
            {octadesk?.efetiva
              ? 'Os convites por e-mail e por WhatsApp saem automaticamente — o WhatsApp pelo número oficial da empresa.'
              : 'Os convites por e-mail saem automaticamente; os de WhatsApp você dispara pelo botão do painel.'}{' '}
            Depois do envio a cotação <strong>não pode mais ser editada</strong>.
            {/*
              at-16: a foto vai na prévia do link e no topo da tela do fornecedor. Enviar sem ela
              é permitido (há requisição sem foto), mas não pode passar despercebido.
            */}
            {estadoDaFoto?.haFotosParaEscolher && !estadoDaFoto.escolhida && (
              <span className="aviso-de-tela" style={{ display: 'block', marginTop: 12 }}>
                <strong>Nenhuma imagem do veículo selecionada ainda.</strong> O link sai só com a placa.{' '}
                <button
                  type="button"
                  className="aviso-de-tela-link"
                  onClick={() => {
                    setConfirmacao(null);
                    setPedidoDeEscolhaDaFoto((n) => n + 1);
                  }}
                >
                  Escolher a foto agora
                </button>
              </span>
            )}
            {estadoDaFoto?.erro && (
              <span className="aviso-de-tela" style={{ display: 'block', marginTop: 12 }}>
                Não foi possível verificar se há foto do veículo escolhida.
              </span>
            )}
          </>
        }
        rotuloConfirmar="Enviar convites"
        carregando={processando}
        onConfirmar={enviarConvites}
        onCancelar={() => setConfirmacao(null)}
      />

      <ConfirmacaoModal
        isOpen={confirmacao === 'encerrar'}
        titulo="Encerrar o recebimento de propostas?"
        descricao={
          <>
            {naoResponderam.length > 0 && (
              <>
                <strong>{naoResponderam.join(', ')}</strong> ainda não respondeu.{' '}
              </>
            )}
            Os fornecedores deixam de poder responder ou corrigir a proposta, e a comparação é liberada para a
            negociação.
            {/*
              A pergunta do Phelipe (reunião de 29/10/2026, at-18): com item sem oferta, o comprador decide
              aqui se chama outro fornecedor antes de encerrar. Encerrar mesmo assim continua possível.
            */}
            {semOferta.length > 0 && (
              <span className="aviso-de-tela" style={{ display: 'block', marginTop: 12 }}>
                <strong>
                  {semOferta.length === 1 ? '1 item ficou sem oferta' : `${semOferta.length} itens ficaram sem oferta`}:
                </strong>{' '}
                {semOferta.map((i) => i.descricao).join('; ')}.{' '}
                <button
                  type="button"
                  className="aviso-de-tela-link"
                  onClick={() => {
                    setConfirmacao(null);
                    convidar(semOferta.map((i) => i.cotacaoItemId));
                  }}
                >
                  Convidar outro fornecedor antes
                </button>
              </span>
            )}
          </>
        }
        rotuloConfirmar={semOferta.length > 0 ? 'Encerrar assim mesmo' : 'Encerrar e iniciar negociação'}
        carregando={processando}
        onConfirmar={() => executar(() => encerrarRespostas(cotacao.id), 'Respostas encerradas.')}
        onCancelar={() => setConfirmacao(null)}
      />

      <ConfirmacaoModal
        isOpen={confirmacao === 'reabrir'}
        titulo="Reabrir para respostas?"
        descricao={
          <>
            A cotação volta a receber propostas até a data escolhida: os fornecedores podem responder e corrigir o
            que mandaram, e você pode convidar outro fornecedor. As escolhas já feitas na comparação ficam como estão.
            <label style={{ display: 'block', marginTop: 12 }}>
              Novo prazo de respostas{' '}
              <input
                type="date"
                aria-label="Novo prazo de respostas"
                value={novoPrazo}
                onChange={(e) => setNovoPrazo(e.target.value)}
              />
            </label>
          </>
        }
        rotuloConfirmar="Reabrir"
        desabilitado={!novoPrazo}
        carregando={processando}
        onConfirmar={() => executar(() => reabrirCotacao(cotacao.id, novoPrazo), 'Cotação reaberta para respostas.')}
        onCancelar={() => setConfirmacao(null)}
      />

      <ConfirmacaoModal
        isOpen={confirmacao === 'recotar'}
        titulo="Recotar os itens sem compra?"
        descricao={
          <>
            Nasce uma <strong>cotação nova</strong>, em rascunho, só com{' '}
            {itensParaRecotar.length === 1 ? 'o item' : `os ${itensParaRecotar.length} itens`} que ficaram sem compra:{' '}
            {itensParaRecotar.map((i) => i.descricao).join('; ')}. Você escolhe os fornecedores nela antes de enviar. Esta
            cotação não muda.
          </>
        }
        rotuloConfirmar="Criar a cotação nova"
        carregando={processando}
        onConfirmar={recotar}
        onCancelar={() => setConfirmacao(null)}
      />

      <ConfirmacaoModal
        isOpen={confirmacao === 'fechar-cesta'}
        titulo="Fechar a cesta?"
        descricao={
          totaisCesta.divergentes > 0 ? (
            <>
              <strong>{totaisCesta.divergentes} item(ns)</strong> acima do menor preço vão para a fila do gestor de
              compras. <strong>Nenhum pedido é gerado</strong> enquanto houver item pendente.
            </>
          ) : (
            <>
              Todos os itens estão no menor preço. Serão gerados{' '}
              <strong>{cestaPorFornecedor.length} pedido(s)</strong> no ERP, um por fornecedor vencedor.
            </>
          )
        }
        rotuloConfirmar="Fechar cesta"
        carregando={processando}
        onConfirmar={() =>
          executar(async () => {
            const r = await fecharCesta(cotacao.id);
            setAba('painel');
            return r;
          }, 'Cesta fechada.')
        }
        onCancelar={() => setConfirmacao(null)}
      />

      <GerarPedidosModal
        isOpen={confirmacao === 'gerar-pedidos'}
        cotacaoId={cotacao.id}
        numero={cotacao.numero}
        carregando={processando}
        onConfirmar={(formas) => executar(() => gerarPedidos(cotacao.id, formas), 'Pedidos processados.')}
        onCancelar={() => setConfirmacao(null)}
      />

      {/*
        Os dois e-mails ao fornecedor (at-11). Um modal só, parametrizado: o que muda entre
        eles é o texto e a rota, não o fluxo. O destinatário aparece ANTES de disparar —
        e-mail é irreversível, e o endereço errado só se descobre quando o fornecedor não
        responde.
      */}
      <ConfirmacaoModal
        isOpen={confirmacaoEmail !== null}
        titulo={
          confirmacaoEmail?.finalidade === 'ESPELHO_NF'
            ? 'Solicitar o espelho da nota fiscal?'
            : confirmacaoEmail?.finalidade === 'RESUMO_PROPOSTA'
              ? 'Enviar o resumo da proposta?'
              : 'Autorizar o faturamento por e-mail?'
        }
        descricao={
          <>
            <p style={{ margin: '0 0 10px' }}>
              {confirmacaoEmail?.finalidade === 'RESUMO_PROPOSTA' ? (
                <>
                  Será enviado um e-mail para <strong>{confirmacaoEmail?.fornecedorNome}</strong> com o resumo da
                  proposta que ele respondeu: cada peça, a condição, o preço, o prazo, o frete e a condição de
                  pagamento, para ele conferir.
                </>
              ) : confirmacaoEmail?.finalidade === 'ESPELHO_NF' ? (
                <>
                  Será enviado um e-mail para <strong>{confirmacaoEmail?.fornecedorNome}</strong> pedindo a
                  prévia da nota fiscal com <strong>os itens que ele venceu</strong> e os valores fechados.
                </>
              ) : (
                <>
                  Será enviado um e-mail para <strong>{confirmacaoEmail?.fornecedorNome}</strong> autorizando o
                  faturamento do pedido, com os itens e a previsão de entrega.
                </>
              )}
            </p>
            <p style={{ margin: '0 0 10px' }}>
              {/*
                Prévia, e a tela diz que é: quem escolhe o contato é o servidor, e o endereço
                que de fato saiu volta na resposta. Sem o endereço cadastrado, o envio nem
                acontece — o backend recusa orientando o cadastro.
              */}
              {confirmacaoEmail && emailPrevisto(confirmacaoEmail.fornecedorCnpj) ? (
                <>
                  Destinatário: <strong>{emailPrevisto(confirmacaoEmail.fornecedorCnpj)}</strong>
                </>
              ) : (
                'Este fornecedor não tem e-mail cadastrado — cadastre um contato antes de enviar.'
              )}
            </p>
            <p style={{ margin: 0, fontSize: 13 }}>
              O texto vem de Administração do Sistema, na aba E-mails ao fornecedor.
            </p>
          </>
        }
        rotuloConfirmar="Enviar e-mail"
        carregando={processando}
        onConfirmar={() => {
          if (!confirmacaoEmail) return;
          if (confirmacaoEmail.finalidade === 'RESUMO_PROPOSTA') {
            dispararEmail(
              () => enviarEmailResumoProposta(cotacao.id, confirmacaoEmail.fornecedorCnpj),
              'E-mail com o resumo da proposta',
            );
          } else if (confirmacaoEmail.finalidade === 'ESPELHO_NF') {
            dispararEmail(
              () => enviarEmailEspelhoNf(cotacao.id, confirmacaoEmail.fornecedorCnpj),
              'E-mail do espelho da nota fiscal',
            );
          } else {
            dispararEmail(
              () => enviarEmailAutorizacaoFaturamento(confirmacaoEmail.pedidoId!),
              'E-mail de autorização de faturamento',
            );
          }
        }}
        onCancelar={() => setConfirmacaoEmail(null)}
      />

      <ConfirmacaoModal
        isOpen={confirmacao === 'cancelar'}
        titulo="Cancelar a cotação?"
        descricao={
          <>
            <p style={{ margin: '0 0 10px' }}>
              Todos os links dos fornecedores são <strong>invalidados na hora</strong> e a cotação não pode ser
              reaberta. Esta ação não tem volta.
            </p>
            <label className="pc-label" htmlFor="pc-motivo">
              Motivo do cancelamento
            </label>
            <input
              id="pc-motivo"
              className="pc-input"
              value={motivoCancelamento}
              onChange={(e) => setMotivoCancelamento(e.target.value)}
              placeholder="Ex: peças conseguidas em estoque"
            />
          </>
        }
        rotuloConfirmar="Cancelar cotação"
        perigo
        carregando={processando}
        desabilitado={!motivoCancelamento.trim()}
        onConfirmar={() =>
          executar(async () => {
            await cancelarCotacao(cotacao.id, motivoCancelamento.trim());
            navigate('/portal-cotacoes');
          }, 'Cotação cancelada.')
        }
        onCancelar={() => setConfirmacao(null)}
      />

      {/* Proposta recebida: resolve o convite pelo ID na hora do render — o objeto vivo,
          não um guardado antes do último `carregar()`. */}
      {propostaAbertaId &&
        (() => {
          const convite = cotacao.convites.find((c) => c.id === propostaAbertaId);
          return convite && convite.propostas.length > 0 ? (
            <PropostaRecebidaModal
              convite={convite}
              cotacao={cotacao}
              onFechar={() => setPropostaAbertaId(null)}
              onVerHistorico={() => {
                // Fecha antes de abrir o outro: modal filho sobre hospedeiro derruba o hospedeiro.
                setPropostaAbertaId(null);
                setHistoricoConviteId(convite.id);
              }}
            />
          ) : null;
        })()}

      {historicoConviteId && (
        <HistoricoCorrecoesModal conviteId={historicoConviteId} onFechar={() => setHistoricoConviteId(null)} />
      )}

      {convidando && (
        <ConvidarFornecedorModal
          cotacaoId={cotacao.id}
          jaConvidados={cotacao.convites.map((c) => c.fornecedorCnpj)}
          itens={itensEscolhiveis}
          itensIniciais={itensDoConvite}
          onFechar={() => setConvidando(false)}
          onConvidados={() => {
            setConvidando(false);
            carregar();
          }}
        />
      )}

      {/* Ações do convite (at-07). Resolve pelo ID no render, como o modal de proposta acima:
          depois de cada ação o `carregar()` troca o `cotacao`, e um objeto guardado mentiria.
          ⚠️ As ações FECHAM este modal antes de executar — "Reenviar" e "Gerar novo link" abrem
          o `ResultadoDisparoModal`, e modal filho sobre hospedeiro derruba o hospedeiro no
          primeiro clique (armadilha registrada em `03-code-conventions.md`). */}
      {conviteAberto &&
        (() => {
          const convite = cotacao.convites.find((c) => c.id === conviteAberto);
          return convite ? (
            <ConviteAcoesModal
              convite={convite}
              octadesk={octadesk}
              processando={processando}
              onFechar={() => setConviteAberto(null)}
              onEnviarWhatsappServidor={() => enviarWhatsappPeloServidor(convite.id)}
              onWhatsappManual={() => abrirWhatsapp(convite.id)}
              onReenviar={() => reenviar(convite.id)}
              onGerarNovoLink={() => gerarNovoLink(convite.id)}
              onEnviarResumo={
                podeAnexar && convite.propostas.length > 0
                  ? () =>
                      setConfirmacaoEmail({
                        finalidade: 'RESUMO_PROPOSTA',
                        fornecedorCnpj: convite.fornecedorCnpj,
                        fornecedorNome: convite.fornecedor.razaoSocial,
                      })
                  : undefined
              }
            />
          ) : null;
        })()}

      {/* Montagem condicional (padrão de modais do projeto): o estado nasce a cada abertura. */}
      {resultadoDisparo && (
        <ResultadoDisparoModal
          titulo={resultadoDisparo.titulo}
          linhas={resultadoDisparo.linhas}
          link={resultadoDisparo.link}
          linkMudou={resultadoDisparo.linkMudou}
          mensagem={resultadoDisparo.mensagem}
          expiraEm={resultadoDisparo.expiraEm}
          onFechar={() => setResultadoDisparo(null)}
        />
      )}

      <ComoFunciona
        isOpen={ajuda !== null}
        onClose={() => setAjuda(null)}
        titulo={
          ajuda === 'comparacao'
            ? 'Comparação de propostas'
            : ajuda === 'cesta'
              ? 'Revisão da cesta'
              : 'Painel da cotação'
        }
        secoes={ajuda === 'comparacao' ? helpComparacao : ajuda === 'cesta' ? helpCesta : helpPainel}
      />
      </div>
    </div>
  );
};

export default PortalCotacaoDetalhe;
